import type { EventBus } from "../core/EventBus.ts";
import type { AppEventMap } from "../core/EventBus.ts";
import { NEAR_LOSS_HEARTBEAT_MS } from "../core/feelBudget.ts";
import { SFX_PROFILES } from "./AudioProfiles.ts";
import {
  MUSIC_BASE,
  SFX_BASE,
  AMBIENCE_BASE,
  GRIT_GAIN,
  STING_GAIN,
  REPEAT_TIER,
  SFX_DUCK_TRIGGERS,
} from "./AudioLoudness.ts";
import { synthesizeProceduralBuffer } from "./ProceduralSynth.ts";
// Task-001 (2026-ingame-music-rework, AC-3) re-export: `synthesizeProceduralSamples`
// moved to ./ProceduralSynth.ts (so ProceduralMusic.ts can reuse it without a
// circular import against this file); re-exported here so every existing
// import site (e.g. tests/systems/AudioLoudness.test.ts) is unaffected.
export { synthesizeProceduralSamples } from "./ProceduralSynth.ts";
import { ProceduralMusic } from "./ProceduralMusic.ts";

interface AudioManifestEntry {
  name: string;
  path: string;
  type?: string;
}

interface AudioManifest {
  music: AudioManifestEntry[];
  sfx: AudioManifestEntry[];
  /** BL-143 — the boot/start-screen ambience group (optional; loaded into the
   *  buffer map and played on the dedicated ambience lane, never SFX-procedural). */
  boot?: AudioManifestEntry[];
}

/** BL-143 — options for one ambience-lane cue. */
interface AmbiencePlayOptions {
  loop?: boolean;
  fadeInMs?: number;
  gain?: number;
  /** Crossfade length for a loop's seam — the loop blends into itself (no gap). */
  crossfadeMs?: number;
  /** Optional ONE-TIME muffled→clear lowpass sweep at cue start. */
  lowpassFromHz?: number;
  lowpassToHz?: number;
  lowpassSweepMs?: number;
}

/** A live ambience cue. Loops crossfade between overlapping instances summed at
 *  `mix`; one-shots run a single instance. `env` is the cue lifecycle gain
 *  (fade-in to level / fade-out on stop); `filter` the one-time lowpass sweep. */
interface AmbienceCue {
  env: GainNode;
  filter: BiquadFilterNode | null;
  mix: GainNode | null;
  instances: Set<{ source: AudioBufferSourceNode; gain: GainNode | null }>;
  loopTimer: ReturnType<typeof setTimeout> | null;
  buffer: AudioBuffer;
  durationSec: number;
  crossfadeSec: number;
}

/**
 * Loudness bus defaults (PT-19 / D-142). The music master sits deliberately
 * *under* SFX so the theme is a bed, not a competitor for the player's ear.
 * Task-003 (2026-audio-module-audit, AC-4) — `MUSIC_BASE`/`SFX_BASE` (and the
 * boot-ambience `AMBIENCE_BASE`/`GRIT_GAIN`/`STING_GAIN` below) now live in
 * `./AudioLoudness.ts`, the loudness single source of truth (values
 * byte-identical — a relocation, not a retune); see that module's header doc
 * for the full rationale, including why `MUSIC_BASE` stays 0.4 pending the
 * owner ear-check (spec AC-8 / Task-005).
 */
const DEFAULT_MUSIC_VOLUME = 0.6;
const DEFAULT_SFX_VOLUME = 0.9;

/**
 * BL-143 / D-244 (S147) — the boot & start-screen ambience lane. Two
 * owner-provided BULLYFINGER samples ride the existing `decodeAudioData` sample
 * lane (the bounded D-213 carve-out, same as `theme.m4a`) on their OWN gain bus
 * *under* the music: the loading-terminal grit bed and the threat-line alarm
 * sting. The lane tracks the MUSIC mute + volume (this is music-adjacent
 * atmosphere, not SFX) and the master mute silences it like everything else.
 * The per-cue GAIN levels live in `./AudioLoudness.ts` (Task-003); the
 * fade/sweep TIMING below stays here (not a loudness concern). Files are
 * mastered to a steady, headroomed bed (~−21 LUFS, −3 dBFS true-peak); the
 * imported gains sit them in the final balance.
 *
 * Task-001 (2026-audio-polish, AC-2) — the THIRD cue this lane used to carry,
 * the title floppy loop (`title_loop`, its own `TITLE_*` fade/sweep consts +
 * `TITLE_GAIN`), is retired end-to-end: the theme now owns the whole
 * title→menu→deploy arc instead (see the "theme intro/outro FX" section
 * below + `MusicDirector.enterTitle()`).
 */
const GRIT_FADE_IN_MS = 900;
const GRIT_FADE_OUT_MS = 600;
const STING_FADE_IN_MS = 30;

/** S147 follow-up (owner): loops crossfade into themselves so the seam is
 *  gap-/click-free (this also hides AAC's encoder padding). A short, gentle blend. */
const AMBIENCE_XFADE_MS = 280;

// ── Theme intro/outro FX section (Task-001, 2026-audio-polish, AC-4/AC-5) ───
//
// The pre-game theme (the ONLY buffer-backed, non-procedural music track) now
// owns the whole title→menu→deploy arc; a dedicated FX section sits on ITS
// source path only — `musicSource → themeEnvGain → phaser(dry/wet) →
// introLP → musicFilter` (built in `buildThemeFxChain`, wired at
// `startMusicSource`) — the composer (`composerOut → musicFilter`) attaches
// straight to `musicFilter` and structurally bypasses this section entirely.
//
// Owner correction (2026-07-21, binding — supersedes tech-analysis.md's
// pre-P-3 "steady state = wet" reading): the effects exist ONLY inside the
// intro/outro windows; steady state is the CLEAN signal (LPF neutral, phaser
// fully dry). The cheapest honest mechanism for "clean": the dry path stays
// at a constant, never-touched unity gain (so wet=0 is bit-for-bit a
// passthrough) and the LPF simply parks at its fully-open cutoff — no bypass
// routing needed.
//
// Intro (armed at the title, `MusicDirector.enterTitle()` → `playThemeIntro`):
// LPF opens 250 → 16,000 Hz over 4,000 ms (the exact per-cue lowpass-sweep
// primitive the title floppy already shipped, `startAmbienceCue`) while the
// phaser dissolves 80 % → 0 % wet over 6,000 ms — a French-house "DJ" build.
// Outro (the DEPLOY click, `startThemeOutro`): the mirror, 40 % faster — LPF
// closes over `THEME_OUT_MS` with the phaser RETURNING (0 % → 80 % wet, same
// window), and the volume envelope (`themeEnvGain`, source-side — the shared
// bus is deliberately left alone so "run" can bloom on it independently, the
// AC-5 crossfade seam) reaches 0 slightly BEFORE the LPF/phaser finish
// (`THEME_OUT_ENV_MS < THEME_OUT_MS`).
export const THEME_INTRO_LP_FROM_HZ = 250;
export const THEME_INTRO_LP_TO_HZ = 16000;
export const THEME_INTRO_LP_SWEEP_MS = 4000;
export const THEME_INTRO_PHASER_RAMP_MS = 6000;
/** Phaser wet-mix fraction (0 = fully dry/no effect, 1 = fully wet) at the
 *  START of an armed intro — dissolves to `THEME_INTRO_PHASER_WET_TO` over
 *  `THEME_INTRO_PHASER_RAMP_MS`, i.e. "the phaser effect 80 % → 0 %". */
export const THEME_INTRO_PHASER_WET_FROM = 0.8;
/** Steady-state wet fraction once the intro completes — 0 = fully dry, the
 *  "clean signal" P-3 resolves to. */
export const THEME_INTRO_PHASER_WET_TO = 0;
/** Cascaded all-pass biquad stages (French-house phaser, built from
 *  primitives — no native Web Audio phaser node exists). */
export const PHASER_STAGES = 4;
/** LFO rate sweeping every stage's center frequency. */
export const PHASER_LFO_HZ = 0.35;
/** LFO depth — the all-pass stages' frequency swings ± this many Hz around
 *  {@link PHASER_CENTER_HZ}. */
export const PHASER_DEPTH_HZ = 600;
/** The all-pass cascade's center frequency (the LFO's modulation midpoint). */
export const PHASER_CENTER_HZ = 900;
/** Feedback fed from the last all-pass stage back into the first — the
 *  resonant "swoosh" character. Routed through a minimal (inaudible) delay
 *  node, the Web-Audio-spec-required way to close a feedback loop without an
 *  unsupported zero-delay cycle. */
export const PHASER_FEEDBACK = 0.25;
const PHASER_FEEDBACK_DELAY_SEC = 0.001;
/** The DEPLOY outro — the owner's "40 % faster than the 4 s reinfaden",
 *  anchored on the intro's LPF sweep (a named derivation, not a magic
 *  number): both the LPF close and the phaser's return to wet ride this
 *  window. */
export const THEME_OUT_MS = Math.round(THEME_INTRO_LP_SWEEP_MS * 0.6);
/** The volume envelope's own (slightly shorter) window — "leicht unter dem
 *  Lowpass von der Zeitlichkeit": silence lands fractionally before the
 *  LPF/phaser sweep finishes, `THEME_OUT_ENV_MS < THEME_OUT_MS`. */
export const THEME_OUT_ENV_MS = 2200;

// ── Interlude underwater muffle (Task-003, 2026-audio-polish, AC-10) ───────
//
// The D-215 interlude set (skill-card draft, pause, swap — driven entirely by
// `MusicDirector.setInterlude`) gains a real, clearly-audible low-pass on top
// of its existing brightness dim + (pause-only) duck. Unlike the theme's
// `introLP` (source-side, theme-only), `interludeLP` is spliced BUS-SIDE,
// directly after `musicFilter` and before the M/S widener
// (`musicFilter → interludeLP → splitter → …`) — every music source that
// reaches `musicFilter` (the composer via `composerOut`, and any future
// buffer-backed source) is muffled identically; the SFX chain
// (`sfxGain`/`sfxRepeatBus`/the reverb send-return) never touches
// `musicFilter` at all, so UI/SFX cues stay crisp by construction (P-13).
// Parked at the neutral ceiling (practically transparent) until an interlude
// sweeps it down to `INTERLUDE_LP_HZ`; release sweeps it back open — using
// the EXISTING D-215 in/out ramp times the director already owns
// (`INTERLUDE_IN_MS`/`INTERLUDE_OUT_MS`, `MusicDirector.ts`), not new ones.
/** Muffled cutoff (Hz) — resonant enough to read "underwater", clearly
 *  audible (owner: item 4). Ear-check territory (spec P-14). */
export const INTERLUDE_LP_HZ = 700;
/** Neutral (released) cutoff (Hz) — comfortably above all composer/theme
 *  content; the "steady state stays byte-clean" park value, mirroring
 *  `introLP`'s own open-cutoff mechanism. */
export const INTERLUDE_LP_NEUTRAL_HZ = 18000;

/**
 * S147 (owner) — the soft "glass" hover + click cover real buttons AND any
 * element opted in with `data-sfx-btn` (the pregame operator/module cards are
 * clickable `<div>`s, so they carry that hook). Hover re-triggers only on a NEW
 * button (deduped); a light throttle guards against boundary jitter / fast sweeps.
 */
const UI_BUTTON_SELECTOR = 'button, [role="button"], [data-sfx-btn]';
const UI_HOVER_THROTTLE_MS = 50;

/** SFX names whose playback triggers a music-bus duck (important moments).
 *  Deliberately NOT derived from the AudioLoudness.ts ladder (Task-003 scope:
 *  only REPEAT_TIER and SFX_DUCK_TRIGGERS are derived, per the spec) — this
 *  smaller, independently-curated set is unchanged. */
const DUCK_TRIGGERS = new Set([
  "mainframe_hit_heavy",
  "mainframe_hit_critical",
  "boss_stinger",
  "game_over",
  "tower_destroyed",
  "signal_overload",
  "wave_start",
  "wave_complete",
]);

/**
 * BL-120 / D-233 — the AUDIO_BIBLE fatigue system. REPEAT_TIER (the
 * high-frequency, low-priority "ping wall", routed through its own
 * `sfxRepeatBus`) and SFX_DUCK_TRIGGERS (tiers 0–1, which duck that bed) are
 * imported from `./AudioLoudness.ts` (Task-003, 2026-audio-module-audit,
 * AC-4) — derived from the same per-profile category assignment the loudness
 * bands use, instead of two independently hand-kept `Set`s. Membership is
 * byte-identical to the sets this replaces (18 / 13 members).
 *
 * The fire-budget (AUDIO_BIBLE §3.3). `tower.fired` is the only repeated combat
 * event with no throttle — a dense W30 board asks to play ~340 fire-sounds/sec.
 * A per-element minimum interval collapses that into a controlled texture (each
 * element re-triggers no faster than the ear separates anyway) without ever
 * touching the sim. The 4-voice cap stays as the hard ceiling underneath.
 *
 * S143 / BL-129(a): the W30 storm density still grates by ~W29 despite S135's
 * −75 % reduction (more enemies + off-wall + elites since S141). A second,
 * board-wide rolling-rate throttle tightens dynamically when the per-second
 * fire-event count crosses a threshold (only the highest waves cross it; early
 * boards stay punchy). The per-element clock stays as the floor.
 */
const FIRE_THROTTLE_MS = 90;
/** Board-wide rolling-rate ceiling. Above this, every other fire is dropped. */
const FIRE_DENSITY_DROP_THRESHOLD = 120; // audible fires/sec, board-wide
/** Hard ceiling — at extreme density, drop ~2 of every 3 fires. */
const FIRE_DENSITY_HARD_THRESHOLD = 200;
/** Window the rolling rate is measured over. */
const FIRE_DENSITY_WINDOW_MS = 1000;

/**
 * Task-011 (2026-gameplay-reworks, AC-14/O-6, D-325) — the mass-spawn sound.
 * `enemy_spawn` was the ONLY repeating combat sound with no throttle at all
 * (deaths/hits/fires all already had one — see the fire-budget block above).
 * Owner-approved staged plan (`phase2-options.md` §1.4): Stage A ships a
 * parity cooldown + pitch variation (mirrors `lastDeathSoundTime_`'s 200 ms /
 * `lastHitSoundTime_`'s 100 ms shape); Stage C is the AAA density-to-intensity
 * move — above ~3 spawns/sec (the paper's own named number, §1.3 Option C)
 * individual blips are suppressed entirely and a "wave-surge" pressure term
 * docks into the shipped composer energy/tension system instead (one new
 * input to `MusicDirector.setSpawnSurge`, itself folded into
 * `effectiveSceneTone()` exactly like the near-loss `pressure_` overlay —
 * see `MusicDirector.ts`). Both stages are render/audio-only: they read
 * `enemy.spawned` (already fired for UI/comms) and never touch sim RNG/state.
 */
const SPAWN_THROTTLE_MS = 120;
/** Spawns/sec (rolling window) above which individual blips are suppressed in
 *  favour of the aggregated wave-surge — the options paper's own number. */
const SPAWN_DENSITY_THRESHOLD = 3;
/** Window the spawn rate is measured over — same shape as the fire-budget's. */
const SPAWN_DENSITY_WINDOW_MS = 1000;
/** Spawns/sec above the threshold needed to reach full (1.0) surge intensity —
 *  i.e. the surge ramps over this many spawns/sec past the switchover point. */
const SPAWN_SURGE_RATE_SPAN = 5;
/** No new above-threshold spawn within this window releases the surge (decays
 *  the pressure term back to 0) — mirrors the near-loss pressure's clean release. */
const SPAWN_SURGE_RELEASE_MS = 700;
/** ±% playbackRate jitter applied to the Stage-A spawn blip (small, so it
 *  reads as variation, not a chipmunk effect — `playSfx`'s rate clamp is the
 *  hard backstop regardless). */
const SPAWN_RATE_JITTER = 0.08;

/**
 * Web Audio API engine — replaces AudioStub with real audio playback.
 * Gracefully degrades: if audio fails to load, generates procedural SFX.
 *
 * Gain graph (PT-19 / D-142): sources fan into two buses that sum at the master:
 *
 *              ┌── musicGain ──┐
 *   sources ──>│               ├──> masterGain ──> destination
 *              └──  sfxGain  ──┘
 *
 * Music and SFX have independent mute + volume; every method is a safe no-op
 * when `this.ctx` is null (jsdom / Web-Audio-unavailable).
 */

/**
 * Task-001 (2026-audio-module-audit, AC-1) — one entry of the dev/diagnostics
 * audio-unlock trace: the unlock-chain data flow (ctx state changes, gesture
 * arm/disarm, resume attempts + outcomes, music/ambience play/defer/flush,
 * decode fail/retry, native-bridge lifecycle). `t` is `Date.now()`; `kind` is a
 * short dotted tag (e.g. "resume.attempt"); `detail` is a terse human string.
 */
export interface AudioTraceEntry {
  t: number;
  kind: string;
  detail: string;
}

/**
 * Task-001/Task-002 (2026-audio-polish, AC-4/AC-5, tech-analysis.md §2.5
 * Addendum) — the LOCAL `ThemeAudio` Capacitor plugin's JS contract (a
 * structural type — `src/platform/nativeAudio`'s `createThemeAudio()`
 * wrapper satisfies it, so `systems/` never imports Capacitor). Owns the
 * theme on iOS via an AudioKit graph (`AudioPlayer → MoogLadder → Phaser →
 * Fader`) instead of the community plugin's play/stop/setVolume slot — a
 * REAL native phaser + resonant LPF, in place of the Web-Audio primitives
 * this file builds for web/desktop. ALL ramp timings are pre-scheduled
 * NATIVE-side (zipper-free `ramp(to:duration:)`) — there is no bare "play";
 * `playThemeIntro()` is the only start call, so it is what any fresh theme
 * (re)start on iOS uses, armed or not.
 *
 * Task-002 — the Swift plugin (`ios/App/App/ThemeAudioPlugin.swift`) + its
 * SPM/Xcode wiring + the `createThemeAudio()` wrapper landed here; the
 * engine's theme branch now PREFERS this over the OLD community-plugin
 * bridge (`NativeMusicControl`/`setNativeMusic`/`createNativeMusic`,
 * BL-221), which is retired end-to-end (main.ts's lazy native-music arrival
 * calls `createThemeAudio`/`setThemeAudio` instead; the community plugin
 * itself stays wired for the boot grit/sting cues only,
 * `createNativeBootAudio`, untouched).
 */
export interface ThemeAudioControl {
  preloadTheme(): void;
  /** cutoff 250 → 16,000 Hz / 4,000 ms; phaser mix 0.8 → 0 / 6,000 ms; steady
   *  state transparent. The ONLY start call the plugin exposes. */
  playThemeIntro(): void;
  /** cutoff → 250 Hz / 2,400 ms; phaser mix 0 → 0.8 / 2,400 ms; Fader → 0 /
   *  2,200 ms; stop + reset at 2,400 ms — all native-scheduled. */
  startThemeOutro(): void;
  setVolume(volume: number): void;
  stopTheme(): void;
}

/** Task-001 (2026-audio-polish, AC-4/AC-5) — the live theme intro/outro FX
 *  section's nodes (built per theme-source-start by `buildThemeFxChain`,
 *  released when the web source stops). Kept together so `startThemeOutro`
 *  can reach every param it needs to ramp. */
interface ThemeFxNodes {
  envGain: GainNode;
  introLP: BiquadFilterNode;
  phaserWetGain: GainNode;
  lfo: OscillatorNode;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private buffers = new Map<string, AudioBuffer>();
  // BL-220 — track→path for the music entries, so a music buffer that failed to
  // decode at load time (iOS WKWebView can fail `decodeAudioData` on a SUSPENDED
  // context — which is the state during loadManifest, before any gesture) can be
  // RE-DECODED once the context is running. SFX never need this (they fall back
  // to procedural buffers), so only real file-backed music is affected.
  private musicPaths = new Map<string, string>();
  private musicRedecoding = new Set<string>();
  private musicSource: AudioBufferSourceNode | null = null;
  private masterGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  /** BL-120: repeat-tier sub-bus (sfxRepeatBus → sfxGain) the criticals duck. */
  private sfxRepeatBus: GainNode | null = null;
  private sfxMuted_ = false;
  private musicMuted_ = false;
  private sfxVolume_ = DEFAULT_SFX_VOLUME;
  private musicVolume_ = DEFAULT_MUSIC_VOLUME;
  private masterMuted_ = false;
  private fadeOutTimer: ReturnType<typeof setTimeout> | null = null;
  private unsubscribers: (() => void)[] = [];
  private bus: EventBus;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  /** S195/BL-283 — the active near-loss band (0 = safe). */
  private nearLossBand_: 0 | 1 | 2 | 3 = 0;
  private lastDeathSoundTime_ = 0;
  private lastHitSoundTime_ = 0;
  /** BL-120: per-element fire-sound throttle clocks (the fire-budget). */
  private lastFireSoundTime_ = new Map<string, number>();
  /** S143 / BL-129: rolling timestamps of board-wide audible fires for the
   *  density-aware throttle. Ring buffer; oldest pruned per call. */
  private fireTimestamps_: number[] = [];
  /** S143 / BL-129: deterministic-per-render counter so the density drop is a
   *  predictable stride (every 2nd / every 3rd), not a stutter. */
  private fireDensityCounter_ = 0;
  /** Task-011 (AC-14/O-6, D-325) — Stage A spawn-blip cooldown clock. */
  private lastSpawnSoundTime_ = 0;
  /** Task-011 — rolling timestamps of `enemy.spawned`, for the Stage-C
   *  density switchover (same ring-buffer shape as `fireTimestamps_`). */
  private spawnTimestamps_: number[] = [];
  /** Task-011 — the surge's own release clock: cleared on every above-
   *  threshold spawn, fires the "surge over" event if none arrive in time. */
  private spawnSurgeReleaseTimer_: ReturnType<typeof setTimeout> | null = null;
  private uiClickCleanup: (() => void) | null = null;
  // S147: the global button-hover listener + its per-button dedup/throttle state.
  private uiHoverCleanup: (() => void) | null = null;
  private lastHoverEl_: Element | null = null;
  private lastHoverTime_ = 0;
  private activeVoices = new Map<string, AudioBufferSourceNode[]>();
  private static readonly VOICE_CAP = 4;
  private glueComp: DynamicsCompressorNode | null = null;
  private limiter: DynamicsCompressorNode | null = null;
  private reverbConvolver: ConvolverNode | null = null;
  private reverbSend: GainNode | null = null;
  private reverbReturn: GainNode | null = null;
  private duckerGain: GainNode | null = null;
  private musicFilter: BiquadFilterNode | null = null;
  // PT-37: M/S stereo-width stage (the automatable side gain) + the interlude
  // pause-duck. Distinct from the SFX-sidechain `duckerGain` so the two ducks
  // sum cleanly. Both are null in jsdom/headless (every method self-guards).
  private musicWidthGain: GainNode | null = null;
  private interludeDuckGain: GainNode | null = null;
  /** Task-003 (2026-audio-polish, AC-10) — the interlude underwater muffle's
   *  bus-side low-pass, spliced directly after `musicFilter` (see the
   *  constants' doc above). */
  private interludeLP: BiquadFilterNode | null = null;
  // BL-143: the boot/title ambience lane — its own bus into the master
  // (tracking the music mute + volume), plus the live cues keyed by name so a
  // re-render (e.g. the boot language flip) never stacks a second loop.
  private ambienceBus: GainNode | null = null;
  private ambienceCues = new Map<string, AmbienceCue>();
  private ambienceStopTimers = new Set<ReturnType<typeof setTimeout>>();
  /** BL-143 — loops that SHOULD be playing; (re)asserted when the context
   *  resumes. Autoplay-gated browsers start suspended, and a source started
   *  while suspended doesn't reliably play — so loops requested before the first
   *  user gesture are deferred here and started on resume. One-shots are
   *  moment-tied (the threat sting), so they are dropped, never deferred. */
  private desiredAmbience = new Map<string, AmbiencePlayOptions>();
  /** BL-143 (music parity) — the loop that SHOULD be playing; (re)asserted when
   *  the context resumes. The autoplay gate suspends the context until the first
   *  user gesture, and a source started while suspended doesn't reliably play —
   *  AND leaves `musicSource` non-null, so `isMusicPlaying()` reads true and the
   *  later scene change (menu → combat) skips its own restart, leaving the whole
   *  session silent. So music requested before the context is running is DEFERRED
   *  here (exactly like the ambience loops) and started on resume. */
  private desiredMusicTrack: string | null = null;
  private desiredMusicFadeIn = false;
  private desiredMusicFadeMs = 0;
  // Task-002 (2026-audio-polish, AC-4/AC-5) — the iOS theme transport (see
  // ThemeAudioControl's doc): an AudioKit graph (AudioPlayer → MoogLadder →
  // Phaser → Fader) replacing the old community-plugin (AVAudioPlayer)
  // bridge this field's predecessor (`nativeMusic`/`NativeMusicControl`,
  // BL-221) used to delegate the theme to — that bridge + its production
  // factory (`createNativeMusic`) are retired; the community plugin itself
  // stays wired for the boot grit/sting cues only, untouched.
  private themeAudio: ThemeAudioControl | null = null;
  private themeAudioOn_ = false;
  /** Task-001 (AC-4) — one-shot: set by `playThemeIntro`, cleared by
   *  `playMusic`/`playMusicFadeIn` and consumed by `startMusicSource` when it
   *  next builds the theme's web FX chain. Survives the autoplay-gate
   *  deferral (P-11 — the intro anchors at the ACTUAL start). */
  private desiredThemeIntroArmed_ = false;
  /** Task-001 (AC-4/AC-5) — the live theme FX section's nodes, or null when
   *  no theme source is currently playing on the WEB path. */
  private themeFx_: ThemeFxNodes | null = null;
  /** Task-001 (AC-5) — the tracked outro hard-stop timer (AA-19 discipline:
   *  destroy()-reachable, guarded on fire). Owns the theme's stop for the
   *  crossfade seam — `startMusicSource("run")` must not cancel/hard-cut it. */
  private themeOutroTimer_: ReturnType<typeof setTimeout> | null = null;
  /** Task-001 (AC-5) — true while a deploy-triggered theme outro is running
   *  (native or web); read by `MusicDirector.enterCombatSweep()` via
   *  `isThemeOutroInFlight()` to pick the crossfade hand-off variant. */
  private themeOutroInFlight_ = false;
  /** Removes the mounted gesture-resume listeners, or null when none are armed. */
  private disarmGestureResume: (() => void) | null = null;

  // ── Task-001 (2026-ingame-music-rework, AC-3) — the procedural composer ───
  // Registered logical track names play through a `ProceduralMusic` instance
  // instead of a decoded buffer. `composerOut` feeds the documented
  // music-chain head (`composerOut → musicFilter`) so every chain-side
  // music-reactive behavior (brightness/width/duck) applies to it for free.
  // One composer instance total (today: just `"run"`) — a registry-of-names
  // keeps `startMusicSource` route-agnostic without speculating a
  // multi-composer future the spec never asks for.
  private proceduralTracks = new Set<string>();
  private composerOut: GainNode | null = null;
  private composer: ProceduralMusic | null = null;

  // ── Task-001 (AC-1) — dev/diagnostics audio-unlock trace ──────────────────
  // A small bounded ring buffer (D-298: allocation-light, fixed capacity, no
  // growth) recording the unlock chain so a repro is READABLE, not re-derived.
  // Recording itself always runs (cheap; a handful of pushes per session, never
  // per-frame) — what's gated is EXPOSURE: `window.__game.audioTrace()` is
  // wired dev-only (`import.meta.env.DEV`, main.ts) and the native crumb mirror
  // is wired `__NATIVE_SHELL__`-only (main.ts) — AudioEngine itself never
  // imports Capacitor or checks either flag (systems/ stays platform-agnostic,
  // matching the nativeAudio.ts layer note).
  private static readonly TRACE_CAPACITY = 200;
  private trace_: AudioTraceEntry[] = [];
  private traceListener_: ((entry: AudioTraceEntry) => void) | null = null;

  constructor(bus: EventBus) {
    this.bus = bus;
    this.subscribe();
  }

  /** SFX-bus mute state (the in-game "sound off"). */
  get muted(): boolean {
    return this.sfxMuted_;
  }

  get musicMuted(): boolean {
    return this.musicMuted_;
  }

  get masterMuted(): boolean {
    return this.masterMuted_;
  }

  /** Task-001 (AC-1) — a read-only snapshot of the audio-unlock trace (oldest
   *  first, bounded to the last `TRACE_CAPACITY` events). Returns a fresh copy
   *  so callers (e.g. `window.__game.audioTrace()`) can't mutate the internal
   *  ring buffer. */
  getAudioTrace(): AudioTraceEntry[] {
    return [...this.trace_];
  }

  /** Task-001 (AC-1) — subscribe to every trace event AS IT IS RECORDED (pass
   *  `null` to unsubscribe). Used by main.ts to mirror the trace into the
   *  on-device crumb trail under `__NATIVE_SHELL__`; AudioEngine itself never
   *  references Capacitor or `__NATIVE_SHELL__` (stays platform-agnostic). */
  onAudioTrace(listener: ((entry: AudioTraceEntry) => void) | null): void {
    this.traceListener_ = listener;
  }

  private trace(kind: string, detail = ""): void {
    const entry: AudioTraceEntry = { t: Date.now(), kind, detail };
    this.trace_.push(entry);
    if (this.trace_.length > AudioEngine.TRACE_CAPACITY) this.trace_.shift();
    this.traceListener_?.(entry);
  }

  /**
   * Task-001 (AC-1) — the ONE traced choke point for every `ctx.resume()` call
   * site (resumeContext, playSfx, playMusic/playMusicFadeIn, playAmbience,
   * startMusicSource). Records the attempt + (once settled) its outcome.
   * `Promise.resolve(...)` defensively tolerates a test stub whose `resume()`
   * doesn't return a real Promise; a real AudioContext always does. Fire-and-
   * forget by design — callers never await (the context's own `onstatechange`
   * is what reacts to "running"), so this also prevents an unhandled
   * rejection when a resume attempt is refused (H1a: an untrusted gesture).
   */
  private attemptResume(source: string): void {
    if (!this.ctx) return;
    this.trace("resume.attempt", `${source} state=${this.ctx.state}`);
    try {
      Promise.resolve(this.ctx.resume()).then(
        () => this.trace("resume.resolved", `${source} state=${this.ctx?.state}`),
        (err: unknown) => this.trace("resume.rejected", `${source} ${String(err)}`),
      );
    } catch (err) {
      // A synchronous throw (not spec-compliant, but defend anyway).
      this.trace("resume.rejected", `${source} sync ${String(err)}`);
    }
  }

  async loadManifest(manifest: AudioManifest): Promise<void> {
    try {
      this.ctx = new AudioContext();
      this.trace("ctx.state", `initial=${this.ctx.state}`);
      // BL-143: autoplay-gated browsers start the context "suspended"; a source
      // started while suspended doesn't reliably play. Re-assert the desired
      // ambience loops the moment the context (re)enters "running" — i.e. the
      // first user gesture (click/keydown, which createSystems already resumes).
      this.ctx.onstatechange = () => {
        this.trace("ctx.state", `changed=${this.ctx?.state}`);
        if (this.ctx?.state === "running") {
          // Task-001 (AC-1 / H1 fix): the context is now VERIFIED running —
          // this (not the mere fact SOME gesture fired) is what disarms the
          // re-armable gesture listeners. See armGestureResume().
          this.disarmGestureResume?.();
          this.flushDesiredAmbience();
          this.flushDesiredMusic();
        }
      };

      // Master chain: masterGain → glueComp (2:1 glue) → limiter (brick-wall) → destination
      this.masterGain = this.ctx.createGain();
      this.glueComp = this.ctx.createDynamicsCompressor();
      this.glueComp.threshold.value = -18;
      this.glueComp.knee.value = 12;
      this.glueComp.ratio.value = 2;
      this.glueComp.attack.value = 0.05;
      this.glueComp.release.value = 0.25;
      this.limiter = this.ctx.createDynamicsCompressor();
      this.limiter.threshold.value = -1;
      this.limiter.knee.value = 0;
      this.limiter.ratio.value = 20;
      this.limiter.attack.value = 0.001;
      this.limiter.release.value = 0.05;
      this.masterGain.connect(this.glueComp);
      this.glueComp.connect(this.limiter);
      this.limiter.connect(this.ctx.destination);

      // SFX bus → masterGain (dry path)
      this.sfxGain = this.ctx.createGain();
      this.sfxGain.connect(this.masterGain);

      // BL-120: the repeat-tier bed (tower fire / hits / death / economy) feeds
      // the SFX bus through its own gain so a critical/important cue can duck it
      // (triggerSfxDuck) and read clearly over the constant texture. Unity by
      // default; both the dry and reverb paths still apply downstream.
      this.sfxRepeatBus = this.ctx.createGain();
      this.sfxRepeatBus.connect(this.sfxGain);

      // Reverb send: sfxGain → reverbSend → convolver(procIR) → reverbReturn → masterGain
      this.reverbSend = this.ctx.createGain();
      this.reverbSend.gain.value = 0.15;
      this.reverbConvolver = this.ctx.createConvolver();
      this.reverbConvolver.buffer = this.generateImpulseResponse();
      this.reverbReturn = this.ctx.createGain();
      this.reverbReturn.gain.value = 0.25;
      this.sfxGain.connect(this.reverbSend);
      this.reverbSend.connect(this.reverbConvolver);
      this.reverbConvolver.connect(this.reverbReturn);
      this.reverbReturn.connect(this.masterGain);

      // Music bus (PT-37): musicSource → musicFilter (highshelf) → interludeLP
      //   (Task-003) → [M/S widener] → musicGain → interludeDuckGain →
      //   duckerGain → masterGain
      this.musicFilter = this.ctx.createBiquadFilter();
      this.musicFilter.type = "highshelf";
      this.musicFilter.frequency.value = 2000;
      this.musicFilter.gain.value = 0;
      // Task-003 (2026-audio-polish, AC-10) — the interlude underwater-muffle
      // low-pass, bus-side right after musicFilter (see its constants' doc):
      // this hits the composer (`composerOut → musicFilter`) AND any future
      // buffer-backed music source identically, while the SFX chain (which
      // never connects through musicFilter) stays untouched. Parked at the
      // neutral ceiling — steady state stays byte-clean, like `introLP`.
      this.interludeLP = this.ctx.createBiquadFilter();
      this.interludeLP.type = "lowpass";
      this.interludeLP.frequency.value = INTERLUDE_LP_NEUTRAL_HZ;
      this.musicGain = this.ctx.createGain();
      this.interludeDuckGain = this.ctx.createGain(); // pause-duck (unity by default)
      this.duckerGain = this.ctx.createGain();

      // ── Mid/Side stereo widener (PT-37) ──────────────────────────────────
      // M = (L+R)/2, S = (L−R)/2; out = M ± width·S. The single automatable
      // param is the side gain (`musicWidthGain`). Mono source → L==R → S==0,
      // so the widener is a perfect no-op regardless of width (mono-safe by
      // construction). One cohesive module, built here and driven by the one
      // `setMusicWidth` method.
      const splitter = this.ctx.createChannelSplitter(2);
      const merger = this.ctx.createChannelMerger(2);
      // Mid: 0.5·(L+R) — both channels share the same 0.5 gain.
      const mid = this.ctx.createGain();
      mid.gain.value = 0.5;
      // Side: 0.5·(L−R) — feed L directly and a negated R into a 0.5 sum node.
      const side = this.ctx.createGain();
      side.gain.value = 0.5;
      const rNeg = this.ctx.createGain();
      rNeg.gain.value = -1;
      // The width-controlled side gain (1.0 = neutral stereo) and its inverse
      // for the right channel (out_R = M − width·S).
      this.musicWidthGain = this.ctx.createGain();
      this.musicWidthGain.gain.value = 1;
      const sideNeg = this.ctx.createGain();
      sideNeg.gain.value = -1;

      this.musicFilter.connect(this.interludeLP);
      this.interludeLP.connect(splitter);
      splitter.connect(mid, 0);
      splitter.connect(mid, 1);
      splitter.connect(side, 0);
      splitter.connect(rNeg, 1);
      rNeg.connect(side);
      side.connect(this.musicWidthGain);
      this.musicWidthGain.connect(sideNeg);
      // out_L = M + width·S ; out_R = M − width·S
      mid.connect(merger, 0, 0);
      mid.connect(merger, 0, 1);
      this.musicWidthGain.connect(merger, 0, 0);
      sideNeg.connect(merger, 0, 1);

      merger.connect(this.musicGain);
      this.musicGain.connect(this.interludeDuckGain);
      this.interludeDuckGain.connect(this.duckerGain);
      this.duckerGain.connect(this.masterGain);

      // BL-143: the boot/title ambience lane sums into the master alongside the
      // music + SFX buses; its level mirrors the music mute + volume.
      this.ambienceBus = this.ctx.createGain();
      this.ambienceBus.connect(this.masterGain);

      this.applyMusicGain(true);
      this.applyAmbienceGain(true);
      this.applySfxGain();
    } catch {
      console.warn("[AudioEngine] Web Audio API not available");
      return;
    }

    // BL-220 — remember the music paths so a decode that fails while the context
    // is suspended (iOS) can be retried on resume.
    for (const entry of manifest.music) {
      this.musicPaths.set(entry.name, entry.path);
    }

    const allEntries = [
      ...manifest.music,
      ...manifest.sfx,
      ...(manifest.boot ?? []),
    ];
    const loadPromises = allEntries.map((entry) => this.loadAudio(entry));
    await Promise.allSettled(loadPromises);

    // Generate procedural fallbacks for any missing/failed buffers
    for (const [name, profile] of Object.entries(SFX_PROFILES)) {
      if (!this.buffers.has(name)) {
        this.buffers.set(name, synthesizeProceduralBuffer(this.ctx, profile));
        console.warn(`[AudioEngine] ${name} using procedural fallback`);
      }
    }
  }

  private async loadAudio(entry: AudioManifestEntry): Promise<void> {
    if (!this.ctx) return;
    try {
      const response = await fetch(entry.path);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const arrayBuffer = await response.arrayBuffer();
      if (arrayBuffer.byteLength <= 44) {
        throw new Error(
          `File too small (${arrayBuffer.byteLength} bytes) — likely empty/header-only`,
        );
      }
      const audioBuffer = await this.ctx.decodeAudioData(arrayBuffer);
      this.buffers.set(entry.name, audioBuffer);
    } catch (err) {
      this.trace("decode.fail", `${entry.name} ${String(err)}`);
      console.warn(
        `[AudioEngine] Failed to load: ${entry.name}, will use procedural fallback`,
      );
    }
  }

  /** Procedural "server-room" reverb impulse (stereo noise × exponential decay). */
  private generateImpulseResponse(): AudioBuffer {
    const ctx = this.ctx!;
    const sr = ctx.sampleRate;
    const length = Math.floor(sr * 1.2);
    const buffer = ctx.createBuffer(2, length, sr);
    const left = buffer.getChannelData(0);
    const right = buffer.getChannelData(1);
    for (let i = 0; i < length; i++) {
      const t = i / sr;
      const decay = Math.exp(-4.5 * t);
      left[i] = (Math.random() * 2 - 1) * decay;
      right[i] = (Math.random() * 2 - 1) * decay;
    }
    return buffer;
  }

  /** Resume the AudioContext — call on the first user gesture. Idempotent and
   *  safe to call repeatedly: it is NOT latched on the first attempt, so if an
   *  early resume fails to take (a not-yet-trusted gesture), a later gesture
   *  re-tries. The onstatechange handler flushes the deferred ambience + music
   *  once the context actually reaches "running". Task-001 (AC-1 / H2 fix):
   *  guards on `state !== "running"`, not `=== "suspended"` — WebKit parks an
   *  interrupted context in the non-standard "interrupted" state, which the
   *  old guard silently ignored (matches the broader checks already used by
   *  playMusic/playMusicFadeIn/playAmbience). */
  resumeContext(): void {
    if (!this.ctx) return;
    if (this.ctx.state !== "running") {
      this.attemptResume("resumeContext");
    }
  }

  /**
   * BL-216 — arm a re-armable set of gesture listeners that resume the
   * AudioContext on user input. Idempotent while armed; no-op headless.
   *
   * Task-001 (AC-1 / H1 fix): TWO changes from the original one-shot design.
   * (1) Listens on activation-GRANTING event types too (`pointerup`/
   *     `touchend`/`click`), not just the "start" types — per the HTML
   *     user-activation model a *touch's* `pointerdown`/`touchstart` does NOT
   *     grant transient activation (activation lands on `pointerup`/
   *     `touchend`/`click`), so the very first tap on a touch device could
   *     consume the old one-shot listeners with a resume that never actually
   *     took, leaving no armed retry — the suspected root cause of "only
   *     starts after pausing". (2) Does NOT disarm on the mere ATTEMPT
   *     (a gesture happening); it stays armed until the context is VERIFIED
   *     running, which `loadManifest`'s `ctx.onstatechange` handler signals by
   *     calling `disarmGestureResume()` directly once `state === "running"`.
   *     A rejected/ignored resume therefore leaves the listeners live for the
   *     next gesture to retry, instead of dying with the first one.
   *     `{ passive: true }` throughout — no hot-path input latency added.
   */
  armGestureResume(): void {
    if (typeof document === "undefined") return;
    if (this.disarmGestureResume) return;
    if (this.ctx && this.ctx.state === "running") return; // nothing to unlock
    const events = [
      "pointerdown",
      "pointerup",
      "touchstart",
      "touchend",
      "click",
      "keydown",
    ] as const;
    const disarm = (): void => {
      for (const evt of events) document.removeEventListener(evt, onGesture);
      this.disarmGestureResume = null;
      // Neutral tag — the PRECEDING trace entry (a "ctx.state changed=running"
      // from onstatechange, or none at all on a destroy() teardown) explains
      // why. Called from both places, so this stays reason-agnostic.
      this.trace("gesture.disarm");
    };
    const onGesture = (): void => {
      this.resumeContext();
    };
    for (const evt of events) {
      document.addEventListener(evt, onGesture, { passive: true });
    }
    this.disarmGestureResume = disarm;
    this.trace("gesture.arm", `events=${events.length}`);
  }

  /** Pull the music-bus ducker down then release (keyed SFX → clarity). */
  triggerDuck(): void {
    if (!this.ctx || !this.duckerGain) return;
    const now = this.ctx.currentTime;
    this.duckerGain.gain.cancelScheduledValues(now);
    this.duckerGain.gain.setValueAtTime(this.duckerGain.gain.value, now);
    this.duckerGain.gain.linearRampToValueAtTime(0.3, now + 0.02);
    this.duckerGain.gain.setTargetAtTime(1.0, now + 0.02, 0.1);
  }

  /**
   * BL-120: duck the repeat-tier bed (tower fire / hits / death / economy) so an
   * important cue cuts through the texture, then release. Mirrors `triggerDuck`
   * but on `sfxRepeatBus` — distinct from the music sidechain so they don't
   * fight. Safe no-op without a context.
   */
  triggerSfxDuck(): void {
    if (!this.ctx || !this.sfxRepeatBus) return;
    const now = this.ctx.currentTime;
    this.sfxRepeatBus.gain.cancelScheduledValues(now);
    this.sfxRepeatBus.gain.setValueAtTime(this.sfxRepeatBus.gain.value, now);
    this.sfxRepeatBus.gain.linearRampToValueAtTime(0.45, now + 0.02);
    this.sfxRepeatBus.gain.setTargetAtTime(1.0, now + 0.04, 0.12);
  }

  /**
   * Task-011 (2026-gameplay-reworks, AC-14/O-6, D-325) — Stage C. Above the
   * spawn-density switchover, individual `enemy_spawn` blips are suppressed
   * (the caller already returned before playing one) and this instead emits
   * an `audio.spawnSurge` bus event carrying a 0..1 intensity derived from
   * how far past the threshold the live rate sits. `MusicDirector` is the
   * ONLY subscriber (its own `setSpawnSurge`, folded into
   * `effectiveSceneTone()` next to the existing near-loss `pressure_`
   * overlay) — keeping the density→pressure model single-owner there, per
   * the options paper's own risk note (§1.3 Option C). AudioEngine never
   * calls `setMusicEnergy` directly for this; it only reports density. A
   * release timer re-emits intensity 0 if no further above-threshold spawn
   * arrives within `SPAWN_SURGE_RELEASE_MS`, so the surge always resolves
   * cleanly instead of hanging at its last value.
   */
  private triggerSpawnSurge(liveRate: number): void {
    const intensity = Math.min(
      1,
      (liveRate - SPAWN_DENSITY_THRESHOLD) / SPAWN_SURGE_RATE_SPAN,
    );
    this.bus.emit("audio.spawnSurge", { intensity });
    if (this.spawnSurgeReleaseTimer_) {
      clearTimeout(this.spawnSurgeReleaseTimer_);
    }
    this.spawnSurgeReleaseTimer_ = setTimeout(() => {
      this.spawnSurgeReleaseTimer_ = null;
      this.bus.emit("audio.spawnSurge", { intensity: 0 });
    }, SPAWN_SURGE_RELEASE_MS);
  }

  playSfx(name: string, worldX?: number, opts?: { rate?: number }): void {
    // Task-001 (AC-1 / H1b + H2 fix): the resume attempt now runs BEFORE the
    // sfx-mute early-return below (moved from after it), so a player who is
    // SFX-muted (or mutes before their first tap) still unlocks the context on
    // interaction — the old ordering meant a muted-SFX session had NO path to
    // ever wake a suspended/interrupted context via SFX at all. `state !==
    // "running"` (not `=== "suspended"`) also resumes WebKit's "interrupted"
    // state (H2), matching playMusic/playMusicFadeIn/playAmbience.
    if (this.ctx && this.ctx.state !== "running") {
      this.attemptResume(`playSfx:${name}`);
    }
    if (this.sfxMuted_ || !this.ctx || !this.sfxGain) return;
    const buffer = this.buffers.get(name);
    if (!buffer) return;

    // Voice management: per-sound concurrency cap, steal oldest
    const voices = this.activeVoices.get(name) ?? [];
    if (voices.length >= AudioEngine.VOICE_CAP) {
      const oldest = voices.shift();
      try {
        oldest?.stop();
      } catch {
        /* already stopped */
      }
    }

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;

    // Anti-fatigue: random detune per play
    const profile = SFX_PROFILES[name];
    if (profile?.detuneRange) {
      source.detune.value = (Math.random() * 2 - 1) * profile.detuneRange;
    }

    // S195/BL-281 — optional per-play pitch scale (the kill-tick combo ladder
    // + the crit layer). Multiplies playbackRate so the same tiny buffer can
    // climb a semitone ladder with zero new synthesis; clamped to a musical
    // two-octave band so a bad caller can never chipmunk the mix.
    if (opts?.rate !== undefined) {
      source.playbackRate.value = Math.max(0.5, Math.min(2, opts.rate));
    }

    // BL-120: repeat-tier sounds feed the duckable bed; everything else takes
    // the direct SFX path (so criticals/feedback stay un-ducked over the bed).
    const dest =
      REPEAT_TIER.has(name) && this.sfxRepeatBus
        ? this.sfxRepeatBus
        : this.sfxGain;

    // Spatial panning by board-x position
    let panner: StereoPannerNode | null = null;
    if (worldX !== undefined) {
      panner = this.ctx.createStereoPanner();
      panner.pan.value = Math.max(-1, Math.min(1, (worldX - 480) / 480));
      source.connect(panner);
      panner.connect(dest);
    } else {
      source.connect(dest);
    }

    // Track voice for cap enforcement + self-cleanup on end
    voices.push(source);
    this.activeVoices.set(name, voices);
    source.onended = () => {
      const list = this.activeVoices.get(name);
      if (list) {
        const idx = list.indexOf(source);
        if (idx >= 0) list.splice(idx, 1);
      }
      // Release graph nodes — onended fires once per play, so without this a
      // long session accumulates dangling sources and (especially) panners.
      try {
        source.disconnect();
        panner?.disconnect();
      } catch {
        /* already disconnected */
      }
    };

    source.start(0);

    if (DUCK_TRIGGERS.has(name)) {
      this.triggerDuck();
    }
    if (SFX_DUCK_TRIGGERS.has(name)) {
      this.triggerSfxDuck();
    }
  }

  /** Start a looping track immediately at full bus gain (no fade). Deferred
   *  across the autoplay gate (see `desiredMusicTrack`): if the context is not
   *  yet running, the track is remembered and started on resume. */
  /**
   * Task-002 (2026-audio-polish, AC-4/AC-5) — wire the `ThemeAudio` plugin
   * (see the interface doc). Once set, every theme start/stop/re-level
   * routes here; a lazy arrival (main.ts's BL-228-style deferred load) takes
   * over immediately if a theme track is already desired — mirrors
   * `playMusic`/`playMusicFadeIn`'s own themeAudio branch below, stopping
   * any live web source first so the takeover never sounds both paths at
   * once (double-playback, the AC-1 H3-H4 residual this bridge's retired
   * `nativeMusic` predecessor also guarded). No-op clearing back to null.
   */
  setThemeAudio(ctrl: ThemeAudioControl | null): void {
    this.themeAudio = ctrl;
    if (
      ctrl &&
      this.desiredMusicTrack &&
      !this.proceduralTracks.has(this.desiredMusicTrack)
    ) {
      const hadWebSource = this.musicSource !== null;
      this.stopWebMusicSource();
      this.trace("themeAudio.takeover", `webSourceLive=${hadWebSource}`);
      ctrl.playThemeIntro();
      this.themeAudioOn_ = true;
    }
  }

  /**
   * Task-001 (2026-ingame-music-rework, AC-3) — register `track` as a
   * PROCEDURAL source: `startMusicSource` hands it to a `ProceduralMusic`
   * composer instead of looking up a decoded buffer. Builds `composerOut`
   * (a `GainNode`) into the documented music-chain head
   * (`composerOut → musicFilter`, ledger §4) the first time it's called, so
   * every chain-side music-reactive behavior (brightness/width/duck)
   * applies to the composer for free. Idempotent (one composer instance
   * total); a safe no-op headless (no `ctx`). `opts.seed` is
   * test-injectable (the composer itself defaults to `Date.now()`);
   * `opts.getLoadLevel` is the PerfGovernor pull (B-6 — no governor API
   * change), injected once here at the composition root.
   */
  registerProceduralMusic(
    track: string,
    opts?: { seed?: number; getLoadLevel?: () => number },
  ): void {
    this.proceduralTracks.add(track);
    if (!this.ctx || !this.musicFilter || this.composer) return;
    this.composerOut = this.ctx.createGain();
    this.composerOut.connect(this.musicFilter);
    this.composer = new ProceduralMusic(this.ctx, this.composerOut, {
      ...(opts?.seed !== undefined ? { seed: opts.seed } : {}),
      ...(opts?.getLoadLevel ? { getLoadLevel: opts.getLoadLevel } : {}),
      trace: (kind, detail) => this.trace(kind, detail),
    });
  }

  playMusic(track: string): void {
    this.cancelFadeOut();
    this.desiredMusicTrack = track;
    this.desiredMusicFadeIn = false;
    this.desiredThemeIntroArmed_ = false; // Task-001 (AC-4) — a plain start never arms the web sweep
    // Task-001/Task-002 (2026-audio-polish, AC-4/AC-5): the ThemeAudio
    // contract owns the theme's iOS transport (both are per-track, never
    // wired for a procedural track like "run" — AA-29(a)).
    if (this.themeAudio && !this.proceduralTracks.has(track)) {
      // Task-002 (M-1) — a fresh native intro supersedes any stale outro
      // bookkeeping from a PREVIOUS deploy: without this, a pending outro
      // timer (armed by startThemeOutro) can fire AFTER this restart and
      // flip themeAudioOn_ false even though the theme is playing again —
      // isMusicPlaying() would misreport. Mirrors startMusicSource's
      // identical reset for the web path ("a FRESH buffer-backed…" below).
      this.clearThemeOutroTimer();
      this.themeOutroInFlight_ = false;
      this.themeAudio.playThemeIntro(); // the only start call the plugin exposes
      this.themeAudioOn_ = true;
      this.trace("music.play", `${track} native=themeAudio`);
      return;
    }
    if (!this.ctx) return;
    if (this.ctx.state !== "running") {
      this.trace("music.defer", track);
      this.attemptResume("playMusic"); // best-effort; only lands inside a user gesture
      return; // onstatechange → flushDesiredMusic starts it on resume
    }
    this.trace("music.play", track);
    this.startMusicNow();
  }

  /** Start a looping track at gain 0 and ramp the music bus up to its target.
   *  Deferred across the autoplay gate, exactly like `playMusic`. */
  playMusicFadeIn(track: string, fadeMs = 1000): void {
    this.cancelFadeOut();
    this.desiredMusicTrack = track;
    this.desiredMusicFadeIn = true;
    this.desiredMusicFadeMs = fadeMs;
    this.desiredThemeIntroArmed_ = false; // Task-001 (AC-4) — a plain start never arms the web sweep
    // Task-001/Task-002 (2026-audio-polish, AC-4/AC-5) — see playMusic's
    // matching comment: the ThemeAudio contract owns the theme's iOS
    // transport (per-track, not "any music" — AA-29(a)).
    if (this.themeAudio && !this.proceduralTracks.has(track)) {
      // Task-002 (M-1) — see playMusic's matching comment: reset any stale
      // outro bookkeeping before this fresh restart.
      this.clearThemeOutroTimer();
      this.themeOutroInFlight_ = false;
      this.themeAudio.playThemeIntro();
      this.themeAudioOn_ = true;
      this.trace("music.play", `${track} fadeMs=${fadeMs} native=themeAudio`);
      return;
    }
    if (!this.ctx || !this.musicGain) return;
    if (this.ctx.state !== "running") {
      this.trace("music.defer", `${track} fadeMs=${fadeMs}`);
      this.attemptResume("playMusicFadeIn"); // best-effort; only lands inside a user gesture
      return; // onstatechange → flushDesiredMusic starts it on resume
    }
    this.trace("music.play", `${track} fadeMs=${fadeMs}`);
    this.startMusicNow();
  }

  /**
   * Task-001 (2026-audio-polish, AC-4) — start the theme with the intro FX
   * ARMED: on web, `startMusicSource` sweeps the LPF/phaser section over the
   * named intro timings as the source starts (`buildThemeFxChain`); on iOS
   * (a `themeAudio` bridge present) this is IDENTICAL to the plain path
   * above (`playThemeIntro()` is the only native start call there is —
   * "armed" only distinguishes the WEB FX chain; the Swift plugin runs its
   * OWN native ramp regardless). Falls back to the deferred Web-Audio path
   * exactly like `playMusicFadeIn` when no native bridge is wired. Deferred
   * across the autoplay gate the same way (P-11 — the intro anchors at the
   * ACTUAL start once flushed, not wall-clock title time).
   */
  playThemeIntro(track: string, fadeMs = THEME_INTRO_LP_SWEEP_MS): void {
    this.cancelFadeOut();
    this.desiredMusicTrack = track;
    this.desiredMusicFadeIn = true;
    this.desiredMusicFadeMs = fadeMs;
    this.desiredThemeIntroArmed_ = true;
    if (this.themeAudio && !this.proceduralTracks.has(track)) {
      // Task-002 (M-1) — see playMusic's matching comment: reset any stale
      // outro bookkeeping before this fresh restart.
      this.clearThemeOutroTimer();
      this.themeOutroInFlight_ = false;
      this.themeAudio.playThemeIntro();
      this.themeAudioOn_ = true;
      this.trace("music.intro", `${track} native=themeAudio`);
      return;
    }
    if (!this.ctx || !this.musicGain) return;
    if (this.ctx.state !== "running") {
      this.trace("music.defer", `${track} fadeMs=${fadeMs}`);
      this.attemptResume("playThemeIntro"); // best-effort; only lands inside a user gesture
      return; // onstatechange → flushDesiredMusic starts it on resume
    }
    this.trace("music.intro", `${track} fadeMs=${fadeMs}`);
    this.startMusicNow();
  }

  /** Start the desired music source NOW (context known running) + apply the
   *  fade-in/immediate bus level. The gain ramp lives here, not in
   *  `playMusicFadeIn`, so it is aligned with the source's actual (possibly
   *  deferred) start rather than ramping a bus with no source behind it. */
  private startMusicNow(): void {
    const track = this.desiredMusicTrack;
    if (!track || !this.ctx || !this.musicGain) return;
    if (!this.startMusicSource(track)) return;
    if (this.desiredMusicFadeIn) {
      const now = this.ctx.currentTime;
      const target = this.effectiveMusicGain();
      this.musicGain.gain.cancelScheduledValues(now);
      this.musicGain.gain.setValueAtTime(0, now);
      this.musicGain.gain.linearRampToValueAtTime(
        target,
        now + Math.max(0, this.desiredMusicFadeMs) / 1000,
      );
    } else {
      this.applyMusicGain(true);
    }
  }

  /** (Re)start the desired music loop once the context is running (the music
   *  twin of `flushDesiredAmbience`). No-op if nothing is desired or a source is
   *  already live. */
  private flushDesiredMusic(): void {
    if (!this.ctx || this.ctx.state !== "running") return;
    if (this.desiredMusicTrack && !this.isMusicPlaying()) {
      this.trace("music.flush", this.desiredMusicTrack);
      this.startMusicNow();
    }
  }

  /** Ramp the music bus to 0, then hard-stop the source (cancel-safe). */
  fadeOutMusic(fadeMs = 800): void {
    // Intentional stop — drop any deferred track so a later context resume does
    // not re-assert music the caller has faded out (enterBoot / defeat / menu).
    this.desiredMusicTrack = null;
    if (this.themeAudio) {
      // Task-001 (2026-audio-polish, AC-4/AC-5) — no plain "fade" verb on the
      // contract (native ramps are only exposed via playThemeIntro/
      // startThemeOutro); an intentional non-outro stop (BACK/defeat/menu)
      // just stops outright. Also silences any live Web-Audio source (AC-1 /
      // H3-H4 residual — reachable e.g. when a WEB fade-out is already in
      // flight and this bridge then takes over) and the composer, so no
      // transport keeps sounding/scheduling through an intentional stop.
      this.cancelFadeOut();
      this.themeAudio.stopTheme();
      this.themeAudioOn_ = false;
      this.clearThemeOutroTimer();
      this.themeOutroInFlight_ = false;
      this.stopWebMusicSource();
      this.composer?.stop();
      return;
    }
    if (!this.ctx || !this.musicGain) {
      this.stopMusic();
      return;
    }
    // Task-001: broadened from `!this.musicSource` so a composer-backed
    // ("run") session — which has no `musicSource` buffer — still fades the
    // shared `musicGain` bus (the composer feeds it) before hard-stopping.
    if (!this.musicSource && !this.composer?.isPlaying()) return;
    this.cancelFadeOut();
    const now = this.ctx.currentTime;
    const secs = Math.max(0, fadeMs) / 1000;
    this.musicGain.gain.cancelScheduledValues(now);
    this.musicGain.gain.setValueAtTime(this.musicGain.gain.value, now);
    this.musicGain.gain.linearRampToValueAtTime(0, now + secs);
    this.fadeOutTimer = setTimeout(
      () => {
        this.fadeOutTimer = null;
        this.stopMusic();
        // Restore the bus level for the next fade-in.
        this.applyMusicGain(true);
      },
      Math.max(0, fadeMs),
    );
  }

  private startMusicSource(track: string): boolean {
    if (!this.ctx || !this.musicGain) return false;

    // AC-5 (2026-audio-polish) — the crossfade seam: a deploy-triggered theme
    // outro already owns the outgoing theme's stop via its own tracked timer
    // (see startThemeOutro); starting "run" on top of it must not hard-cut
    // the dissolving tail (the engine detail tech-analysis.md §2.4 flags —
    // `stopMusic()`'s unconditional web/native theme-stop calls would). Only
    // the composer is defensively stopped (it should never be live here).
    if (this.themeOutroInFlight_ && this.proceduralTracks.has(track)) {
      this.composer?.stop();
    } else {
      this.stopMusic();
    }

    // Task-001 (2026-ingame-music-rework, AC-3) — a procedural (composer-
    // driven) track has no decoded buffer at all; the composer schedules its
    // own voices through composerOut → musicFilter instead. Checked BEFORE
    // the buffer lookup so a procedural name always wins even if a
    // same-named manifest entry existed (route 1 never ships one).
    if (this.proceduralTracks.has(track)) {
      if (!this.composer) return false;
      if (this.ctx.state !== "running") {
        this.attemptResume("startMusicSource");
      }
      this.composer.start();
      return true;
    }

    // Task-001 (2026-audio-polish, AC-4/AC-5) — a FRESH buffer-backed
    // (theme) start supersedes any stale outro bookkeeping — reset discipline
    // before the new source/FX chain is built (steady state, per P-3).
    this.clearThemeOutroTimer();
    this.themeOutroInFlight_ = false;

    const buffer = this.buffers.get(track);
    if (!buffer) {
      // BL-220 — the track has no decoded buffer (most likely an iOS
      // decode-while-suspended failure at load). Now that we're trying to PLAY
      // it the context is running, so a re-decode will succeed; it restarts the
      // music itself on success. Returns false for now (nothing to start yet).
      this.redecodeMusic(track);
      return false;
    }

    // Task-001 (AC-1 / H2 fix, found during verification — not one of the two
    // guards named in the spec, but the SAME hole): reachable when
    // redecodeMusic's async fetch/decode settles after the context has
    // re-suspended/interrupted (e.g. an app-background trip mid-redecode).
    if (this.ctx.state !== "running") {
      this.attemptResume("startMusicSource");
    }

    const armed = this.desiredThemeIntroArmed_;
    this.desiredThemeIntroArmed_ = false; // one-shot, consumed here regardless of outcome

    this.musicSource = this.ctx.createBufferSource();
    this.musicSource.buffer = buffer;
    this.musicSource.loop = true;
    this.themeFx_ = this.buildThemeFxChain(this.musicSource, armed);
    this.musicSource.start(0);
    return true;
  }

  /**
   * Task-001 (2026-audio-polish, AC-4/AC-5) — build the theme's dedicated
   * intro/outro FX section (`source → envGain → phaser(dry/wet) → introLP →
   * musicFilter`) and, when `armed`, schedule the intro sweep. Called once
   * per fresh theme-source start (`startMusicSource`); a fresh graph — never
   * reused across starts — is what makes "reset discipline" (steady state
   * before every start) free: an un-armed build simply never leaves neutral.
   *
   * The dry path stays at a constant, never-touched unity gain (so
   * `phaserWetGain` at 0 is bit-for-bit a passthrough) and `introLP` parks at
   * its fully-open cutoff when not armed — the cheapest honest "clean"
   * mechanism (no separate bypass routing needed).
   */
  private buildThemeFxChain(
    source: AudioBufferSourceNode,
    armed: boolean,
  ): ThemeFxNodes {
    const ctx = this.ctx!;
    const now = ctx.currentTime;

    const envGain = ctx.createGain();
    envGain.gain.setValueAtTime(1, now);

    // Phaser: PHASER_STAGES cascaded all-pass biquads, LFO-modulated around
    // PHASER_CENTER_HZ, with feedback closed through a minimal delay (the
    // Web-Audio-spec-required way to route a cycle without an unsupported
    // zero-delay loop) — dry/wet crossfade pair summed at `introLP`'s input.
    const allpasses: BiquadFilterNode[] = [];
    for (let i = 0; i < PHASER_STAGES; i++) {
      const ap = ctx.createBiquadFilter();
      ap.type = "allpass";
      ap.frequency.value = PHASER_CENTER_HZ;
      allpasses.push(ap);
    }
    for (let i = 0; i < allpasses.length - 1; i++) {
      allpasses[i]!.connect(allpasses[i + 1]!);
    }
    const firstAp = allpasses[0]!;
    const lastAp = allpasses[allpasses.length - 1]!;

    const feedbackDelay = ctx.createDelay();
    feedbackDelay.delayTime.value = PHASER_FEEDBACK_DELAY_SEC;
    const feedbackGain = ctx.createGain();
    feedbackGain.gain.value = PHASER_FEEDBACK;
    lastAp.connect(feedbackDelay);
    feedbackDelay.connect(feedbackGain);
    feedbackGain.connect(firstAp);

    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = PHASER_LFO_HZ;
    const lfoDepth = ctx.createGain();
    lfoDepth.gain.value = PHASER_DEPTH_HZ;
    lfo.connect(lfoDepth);
    for (const ap of allpasses) lfoDepth.connect(ap.frequency);
    lfo.start();

    const phaserWetGain = ctx.createGain();
    phaserWetGain.gain.setValueAtTime(
      armed ? THEME_INTRO_PHASER_WET_FROM : THEME_INTRO_PHASER_WET_TO,
      now,
    );

    const introLP = ctx.createBiquadFilter();
    introLP.type = "lowpass";
    introLP.frequency.setValueAtTime(
      armed ? THEME_INTRO_LP_FROM_HZ : THEME_INTRO_LP_TO_HZ,
      now,
    );

    // Wiring: source → envGain → {dry: introLP directly, wet: allpass
    // cascade → phaserWetGain → introLP} → musicFilter (the composer attaches
    // straight to musicFilter — it structurally bypasses this whole section).
    source.connect(envGain);
    envGain.connect(introLP); // dry path — constant unity, never ramped
    envGain.connect(firstAp); // wet feed
    lastAp.connect(phaserWetGain);
    phaserWetGain.connect(introLP);
    introLP.connect(this.musicFilter ?? this.musicGain!);

    if (armed) {
      introLP.frequency.linearRampToValueAtTime(
        THEME_INTRO_LP_TO_HZ,
        now + THEME_INTRO_LP_SWEEP_MS / 1000,
      );
      phaserWetGain.gain.linearRampToValueAtTime(
        THEME_INTRO_PHASER_WET_TO,
        now + THEME_INTRO_PHASER_RAMP_MS / 1000,
      );
    }

    return { envGain, introLP, phaserWetGain, lfo };
  }

  /**
   * Task-001 (2026-audio-polish, AC-5) — the DEPLOY-triggered theme outro:
   * on iOS (a `themeAudio` bridge present) a single native call owns the
   * whole dissolve (cutoff/phaser/Fader ramps + stop+reset, all native-
   * scheduled); on web the FX section's own nodes ramp — `introLP` closes and
   * the phaser returns over `THEME_OUT_MS`, `themeEnvGain` reaches 0 at the
   * slightly shorter `THEME_OUT_ENV_MS` — then a tracked timer (AA-19: guarded
   * on fire, destroy()-reachable) hard-stops the source at `THEME_OUT_MS`.
   * Deliberately does NOT touch `musicGain` (the shared bus) — that is what
   * lets `enterCombatSweep()`'s crossfade bloom "run" independently while the
   * theme's tail is still audible (the AC-5 seam). A safe no-op when there is
   * no theme currently playing on either transport (`enterCombatSweep()` then
   * falls back to the pre-existing bus-fade hand-off, unchanged).
   */
  startThemeOutro(): void {
    if (this.themeAudio) {
      this.themeAudio.startThemeOutro();
      this.themeOutroInFlight_ = true;
      this.desiredMusicTrack = null;
      this.trace("music.outro", "native");
      this.clearThemeOutroTimer();
      this.themeOutroTimer_ = setTimeout(() => {
        this.themeOutroTimer_ = null;
        this.themeOutroInFlight_ = false;
        this.themeAudioOn_ = false;
      }, THEME_OUT_MS);
      return;
    }
    if (!this.ctx || !this.musicSource || !this.themeFx_) return;
    const fx = this.themeFx_;
    const now = this.ctx.currentTime;

    fx.introLP.frequency.cancelScheduledValues(now);
    fx.introLP.frequency.setValueAtTime(fx.introLP.frequency.value, now);
    fx.introLP.frequency.linearRampToValueAtTime(
      THEME_INTRO_LP_FROM_HZ,
      now + THEME_OUT_MS / 1000,
    );
    fx.phaserWetGain.gain.cancelScheduledValues(now);
    fx.phaserWetGain.gain.setValueAtTime(fx.phaserWetGain.gain.value, now);
    fx.phaserWetGain.gain.linearRampToValueAtTime(
      THEME_INTRO_PHASER_WET_FROM,
      now + THEME_OUT_MS / 1000,
    );
    fx.envGain.gain.cancelScheduledValues(now);
    fx.envGain.gain.setValueAtTime(fx.envGain.gain.value, now);
    fx.envGain.gain.linearRampToValueAtTime(0, now + THEME_OUT_ENV_MS / 1000);

    this.themeOutroInFlight_ = true;
    this.desiredMusicTrack = null;
    this.trace("music.outro", "web");

    const src = this.musicSource;
    this.clearThemeOutroTimer();
    this.themeOutroTimer_ = setTimeout(() => {
      this.themeOutroTimer_ = null;
      this.themeOutroInFlight_ = false;
      // Guard: only stop if nothing else has since (re)started the theme —
      // mirrors sweepTimer_'s "still the scene this fired for" discipline.
      if (this.musicSource === src) {
        this.stopWebMusicSource();
      }
    }, THEME_OUT_MS);
  }

  /** True while a deploy-triggered theme outro is running (native or web) —
   *  read by `MusicDirector.enterCombatSweep()` to pick the crossfade
   *  hand-off variant (AC-5). */
  isThemeOutroInFlight(): boolean {
    return this.themeOutroInFlight_;
  }

  private clearThemeOutroTimer(): void {
    if (this.themeOutroTimer_) {
      clearTimeout(this.themeOutroTimer_);
      this.themeOutroTimer_ = null;
    }
  }

  /**
   * BL-220 — re-fetch + re-decode a music track whose buffer is missing (a load-
   * time `decodeAudioData` failure — typically iOS WKWebView decoding on a
   * still-suspended context). Called lazily the first time we try to PLAY the
   * track, when the context is already running, so the decode succeeds. On
   * success it caches the buffer and, if the track is still the one we want,
   * starts it. Best-effort + de-duplicated; a hard failure just stays silent
   * (same as before) rather than throwing.
   */
  private redecodeMusic(track: string): void {
    if (this.musicRedecoding.has(track)) return;
    const path = this.musicPaths.get(track);
    if (!path || !this.ctx) return;
    this.musicRedecoding.add(track);
    this.trace("decode.retry", track);
    void (async () => {
      try {
        const res = await fetch(path);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const arr = await res.arrayBuffer();
        if (arr.byteLength <= 44) throw new Error("empty/header-only");
        const decoded = await this.ctx!.decodeAudioData(arr);
        this.buffers.set(track, decoded);
        this.trace("decode.retry.ok", track);
        // Still wanted + not already playing → start it now (the deferred path).
        if (this.desiredMusicTrack === track && !this.isMusicPlaying()) {
          this.startMusicNow();
        }
      } catch (err) {
        this.trace("decode.retry.fail", `${track} ${String(err)}`);
        console.warn(`[AudioEngine] music re-decode failed: ${track}`);
      } finally {
        this.musicRedecoding.delete(track);
      }
    })();
  }

  stopMusic(): void {
    if (this.themeAudio) {
      this.themeAudio.stopTheme();
      this.themeAudioOn_ = false;
      this.clearThemeOutroTimer();
      this.themeOutroInFlight_ = false;
    }
    this.stopWebMusicSource();
    // Task-001 (2026-ingame-music-rework, AC-3) — the composer's own hard
    // stop; idempotent (a no-op trace-wise if it wasn't playing), so this is
    // safe to call unconditionally alongside the other two transports (every
    // `startMusicSource` call hard-stops via this method before starting the
    // next track — a live composer must not keep sounding under a fresh
    // buffer/native start, and vice versa).
    this.composer?.stop();
  }

  /** Task-001 (AC-1) — stop just the live Web-Audio source, leaving any native
   *  bridge untouched. Shared by `stopMusic` (both paths), `setThemeAudio`
   *  (stop the web source at takeover) and `fadeOutMusic`'s themeAudio branch
   *  (stop a web source left over from a still-fading-out web start). Task-001
   *  (2026-audio-polish, AC-4) — also stops + releases the theme's FX-section
   *  LFO oscillator (an `OscillatorNode` runs indefinitely once started —
   *  disconnecting alone does not stop it) and the FX node references.
   *  Task-002 (O-1, perf review) — also `disconnect()`s the FX chain's
   *  entry/exit nodes (`envGain`, `introLP`) so their release is
   *  deterministic (not left to GC timing) rather than merely dropping the
   *  JS references. */
  private stopWebMusicSource(): void {
    if (this.musicSource) {
      try {
        this.musicSource.stop();
      } catch {
        /* already stopped */
      }
      this.musicSource = null;
    }
    if (this.themeFx_) {
      try {
        this.themeFx_.lfo.stop();
      } catch {
        /* already stopped */
      }
      this.themeFx_.envGain.disconnect();
      this.themeFx_.introLP.disconnect();
      this.themeFx_ = null;
    }
  }

  private cancelFadeOut(): void {
    if (this.fadeOutTimer) {
      clearTimeout(this.fadeOutTimer);
      this.fadeOutTimer = null;
    }
  }

  private effectiveMusicGain(): number {
    return this.musicMuted_ ? 0 : this.musicVolume_ * MUSIC_BASE;
  }

  private effectiveSfxGain(): number {
    return this.sfxMuted_ ? 0 : this.sfxVolume_ * SFX_BASE;
  }

  /** BL-143: the ambience lane sits under the music — it follows the MUSIC mute
   *  + volume (master mute is applied at the master node, as for every bus). */
  private effectiveAmbienceGain(): number {
    return this.musicMuted_ ? 0 : this.musicVolume_ * AMBIENCE_BASE;
  }

  private applyAmbienceGain(immediate = false): void {
    if (!this.ctx || !this.ambienceBus) return;
    const target = this.effectiveAmbienceGain();
    const now = this.ctx.currentTime;
    this.ambienceBus.gain.cancelScheduledValues(now);
    if (immediate) {
      this.ambienceBus.gain.setValueAtTime(target, now);
    } else {
      this.ambienceBus.gain.setValueAtTime(this.ambienceBus.gain.value, now);
      this.ambienceBus.gain.linearRampToValueAtTime(target, now + 0.06);
    }
  }

  /** Apply the music-bus target. `immediate` sets it now; otherwise a short
   *  (~60 ms) ramp avoids a zipper click when a slider is dragged. */
  private applyMusicGain(immediate = false): void {
    // Task-001 (2026-audio-polish, AC-3/AC-4/AC-5) — keep the ThemeAudio
    // bridge's volume in lockstep with mute/volume (its Fader is the
    // mute/volume target on iOS; "a music-muted session stays silent
    // throughout" incl. setVolume(0)).
    if (this.themeAudio) this.themeAudio.setVolume(this.effectiveMusicGain());
    if (!this.ctx || !this.musicGain) return;
    const target = this.effectiveMusicGain();
    const now = this.ctx.currentTime;
    this.musicGain.gain.cancelScheduledValues(now);
    if (immediate) {
      this.musicGain.gain.setValueAtTime(target, now);
    } else {
      this.musicGain.gain.setValueAtTime(this.musicGain.gain.value, now);
      this.musicGain.gain.linearRampToValueAtTime(target, now + 0.06);
    }
  }

  private applySfxGain(): void {
    if (!this.sfxGain || !this.ctx) return;
    this.sfxGain.gain.value = this.effectiveSfxGain();
  }

  setMusicVolume(vol: number): void {
    this.musicVolume_ = Math.max(0, Math.min(1, vol));
    this.applyMusicGain();
    this.applyAmbienceGain(); // BL-143: the ambience lane scales with the music
    // Task-004 (2026-audio-module-audit, AC-6) — echo the resolved volume back,
    // mirroring setMusicMuted's audio.musicMuteChanged echo below. This is the
    // fix for the HUD-mixer persisted-volume drift: any live AudioMixer widget
    // (incl. one boot constructed with stale defaults, HUD.ts:206-211) reflects
    // whatever the engine's OWN authoritative volume is, regardless of whether
    // this setter was reached via a widget's audio.setMusicVolume request or via
    // main.ts's direct boot-time apply of a persisted MetaState value
    // (main.ts:~1039) — both paths now converge on one echoed truth.
    this.bus.emit("audio.musicVolumeChanged", this.musicVolume_);
  }

  setSfxVolume(vol: number): void {
    this.sfxVolume_ = Math.max(0, Math.min(1, vol));
    this.applySfxGain();
    // Task-004 (AC-6) — see setMusicVolume's comment above; same fix, SFX bus.
    this.bus.emit("audio.sfxVolumeChanged", this.sfxVolume_);
  }

  setMusicMuted(muted: boolean): void {
    this.musicMuted_ = muted;
    this.applyMusicGain();
    this.applyAmbienceGain(); // BL-143: muting the music silences ambience too
    this.bus.emit("audio.musicMuteChanged", muted);
  }

  setSfxMuted(muted: boolean): void {
    this.sfxMuted_ = muted;
    this.applySfxGain();
    this.bus.emit("audio.sfxMuteChanged", muted);
  }

  /** Toggle the SFX bus — the existing HUD "sound off" button. */
  toggleMute(): boolean {
    this.setSfxMuted(!this.sfxMuted_);
    return this.sfxMuted_;
  }

  toggleMusic(): boolean {
    this.setMusicMuted(!this.musicMuted_);
    return this.musicMuted_;
  }

  /**
   * Task-004 (2026-audio-module-audit, AC-5/AC-6 settings sweep) — master mute
   * is DELIBERATELY session-only. Unlike `sfxMuted`/`musicMuted`/the two
   * volumes (persisted, `main.ts`'s `metaState.settings` + its
   * `audio.sfxMuteChanged`/`musicMuteChanged`/`setSfxVolume`/
   * `setMusicVolume` save listeners), no `audio.masterMuteChanged`
   * persistence listener exists and `GameSettings` carries no `masterMuted`
   * field — confirmed by design, not an oversight: `toggleMaster` is the
   * HUD's "MUTE ALL" row and BootSequence's gear "audio.audio" row, both a
   * quick panic/convenience silence-everything affordance, not a saved taste
   * preference — it should not survive a restart into a silent boot. See the
   * ledger (`docs/AUDIO_AUDIT_2026-07.md`) for the full disposition.
   */
  setMasterMuted(muted: boolean): void {
    this.masterMuted_ = muted;
    if (this.masterGain) {
      this.masterGain.gain.value = muted ? 0 : 1;
    }
    this.bus.emit("audio.masterMuteChanged", muted);
  }

  toggleMaster(): boolean {
    this.setMasterMuted(!this.masterMuted_);
    return this.masterMuted_;
  }

  /** High-shelf filter brightness on the music bus (0 = -20 dB cut, 1 = flat). */
  setMusicBrightness(brightness: number, fadeMs = 300): void {
    if (!this.ctx || !this.musicFilter) return;
    const g = -20 * (1 - Math.max(0, Math.min(1, brightness)));
    const now = this.ctx.currentTime;
    this.musicFilter.gain.cancelScheduledValues(now);
    this.musicFilter.gain.setValueAtTime(this.musicFilter.gain.value, now);
    this.musicFilter.gain.linearRampToValueAtTime(
      g,
      now + Math.max(0, fadeMs) / 1000,
    );
  }

  /** Playback rate on the music source (clamped ±6%). Task-001
   *  (2026-ingame-music-rework, AC-3) — forwards to the composer's tempo
   *  scale (same clamp) when it is the live source, so a scene rate nudge
   *  (`MusicDirector`'s boss/defeat presets) reads identically regardless of
   *  which source is playing; still a safe no-op with neither live. */
  setMusicRate(rate: number, fadeMs = 300): void {
    if (!this.ctx) return;
    const r = Math.max(0.94, Math.min(1.06, rate));
    if (this.musicSource) {
      const now = this.ctx.currentTime;
      this.musicSource.playbackRate.cancelScheduledValues(now);
      this.musicSource.playbackRate.setValueAtTime(
        this.musicSource.playbackRate.value,
        now,
      );
      this.musicSource.playbackRate.linearRampToValueAtTime(
        r,
        now + Math.max(0, fadeMs) / 1000,
      );
    }
    if (this.composer?.isPlaying()) {
      this.composer.setTempoScale(r, fadeMs);
    }
  }

  /**
   * Stereo width on the music bus (M/S widener side gain). PT-37.
   * `width` 0 = mono, 1 = neutral stereo, >1 = wider. Clamped to a mono-safe
   * ceiling of 1.4. Ramped exactly like `setMusicBrightness` (cancel + anchor +
   * linear ramp). No-op on a mono source (side ≈ 0) and in headless (null ctx).
   */
  setMusicWidth(width: number, fadeMs = 300): void {
    if (!this.ctx || !this.musicWidthGain) return;
    const w = Math.max(0, Math.min(1.4, width));
    const now = this.ctx.currentTime;
    this.musicWidthGain.gain.cancelScheduledValues(now);
    this.musicWidthGain.gain.setValueAtTime(this.musicWidthGain.gain.value, now);
    this.musicWidthGain.gain.linearRampToValueAtTime(
      w,
      now + Math.max(0, fadeMs) / 1000,
    );
  }

  /**
   * Music-bus interlude duck (PT-37). `db` 0 = unity, negative = attenuation
   * (e.g. −4.5 dB while paused). Lives on a gain distinct from the SFX-sidechain
   * `duckerGain` so the two ducks multiply cleanly. Ramped like the others.
   */
  setMusicDuck(db: number, fadeMs = 300): void {
    if (!this.ctx || !this.interludeDuckGain) return;
    const gain = Math.pow(10, Math.min(0, db) / 20);
    const now = this.ctx.currentTime;
    this.interludeDuckGain.gain.cancelScheduledValues(now);
    this.interludeDuckGain.gain.setValueAtTime(
      this.interludeDuckGain.gain.value,
      now,
    );
    this.interludeDuckGain.gain.linearRampToValueAtTime(
      gain,
      now + Math.max(0, fadeMs) / 1000,
    );
  }

  /**
   * Task-003 (2026-audio-polish, AC-10) — the interlude underwater muffle:
   * sweeps the bus-side `interludeLP` between `INTERLUDE_LP_NEUTRAL_HZ`
   * (`active` false — full release) and `INTERLUDE_LP_HZ` (`active` true).
   * `fadeMs` is the caller's (the director's) — this method invents no ramp
   * timing of its own, it reuses whatever the D-215 interlude machinery
   * already passes (`INTERLUDE_IN_MS`/`INTERLUDE_OUT_MS`,
   * `MusicDirector.setInterlude`). Ramped exactly like `setMusicBrightness`/
   * `setMusicWidth`/`setMusicDuck` (cancel + anchor + linear ramp) — composes
   * with the duck for free (distinct nodes on the same bus). Traces the
   * transition on the existing diagnostics ring buffer (cheap — only called
   * at interlude open/close, never per-frame).
   */
  setMusicMuffle(active: boolean, fadeMs = 300): void {
    if (!this.ctx || !this.interludeLP) return;
    const target = active ? INTERLUDE_LP_HZ : INTERLUDE_LP_NEUTRAL_HZ;
    const now = this.ctx.currentTime;
    this.interludeLP.frequency.cancelScheduledValues(now);
    this.interludeLP.frequency.setValueAtTime(
      this.interludeLP.frequency.value,
      now,
    );
    this.interludeLP.frequency.linearRampToValueAtTime(
      target,
      now + Math.max(0, fadeMs) / 1000,
    );
    this.trace("music.muffle", active ? "on" : "off");
  }

  /**
   * Task-001 (2026-ingame-music-rework, AC-3) — the composer's ONE energy
   * input (0..1), mirroring `setMusicBrightness`'s shape. Forwards to the
   * live composer's layer mixer (smooth per-layer gain fades); a safe no-op
   * without a registered/live composer (the engine-wide null-safety
   * convention — see `MusicEngine`'s optional-method pattern in
   * `MusicDirector.ts`, which is where a future caller wires this in).
   */
  setMusicEnergy(energy: number, fadeMs = 400): void {
    this.composer?.setEnergy(energy, fadeMs);
  }

  /**
   * Task-002 (2026-ingame-music-rework, AC-2) — a narrow PUBLIC trace hook
   * for callers outside AudioEngine (MusicDirector's D-240 hand-off) to
   * record a diagnostics crumb on the SAME ring buffer as every other music
   * trace entry, without exposing the private `trace()` choke point itself.
   */
  traceMusicEvent(kind: string, detail = ""): void {
    this.trace(kind, detail);
  }

  /**
   * AA-29(a) (Task-002, 2026-ingame-music-rework, AC-5) — true for ANY live
   * transport: the iOS theme bridge (`themeAudio`), a live Web-Audio buffer
   * source, or the procedural composer. Per-track gating above means the
   * bridge and the composer can be simultaneously "available" (a live theme
   * bridge plus a registered "run" composer) without the bridge being what's
   * actually audible — this can no longer short-circuit on bridge PRESENCE,
   * only on what is genuinely playing.
   */
  isMusicPlaying(): boolean {
    return (
      this.themeAudioOn_ ||
      this.musicSource !== null ||
      (this.composer?.isPlaying() ?? false)
    );
  }

  // ── BL-143: the boot/title ambience lane ───────────────────────────────────

  /**
   * Start an ambience cue on the boot/title lane. Loops crossfade into
   * themselves (overlapping instances summed through a mix node) so the seam is
   * gap-/click-free; one-shots run once. An optional ONE-TIME lowpass sweep opens
   * a "dumpf" filter over the fade (the primitive the retired `title_loop` cue
   * shipped; `buildThemeFxChain`'s `introLP` reuses the same idea on the theme's
   * OWN dedicated FX section instead of this generic one-shot sweep — Task-001,
   * 2026-audio-polish). Idempotent per name (a re-render never stacks a second
   * loop). Loops requested while the context is suspended (autoplay-gated cold
   * start) are DEFERRED and started on resume; one-shots are moment-tied and
   * dropped. Safe no-op without a context OR buffer (graceful — ambience is
   * never load-fatal).
   */
  playAmbience(name: string, opts: AmbiencePlayOptions = {}): void {
    if (!this.ctx || !this.ambienceBus) return;
    if (opts.loop) this.desiredAmbience.set(name, opts);
    if (this.ctx.state !== "running") {
      this.trace("ambience.defer", name);
      this.attemptResume(`playAmbience:${name}`); // best-effort; only lands inside a user gesture
      return; // onstatechange → flushDesiredAmbience starts the loop on resume
    }
    this.trace("ambience.play", name);
    this.startAmbienceCue(name, opts);
  }

  /** Build the cue graph + start it (called only when the context is running). */
  private startAmbienceCue(name: string, opts: AmbiencePlayOptions): void {
    if (!this.ctx || !this.ambienceBus) return;
    if (this.ambienceCues.has(name)) return; // idempotent
    const buffer = this.buffers.get(name);
    if (!buffer) return; // missing asset → silent (graceful)

    const now = this.ctx.currentTime;
    // Cue lifecycle gain: fade in to the target level (and out on stop).
    const env = this.ctx.createGain();
    const target = opts.gain ?? 1;
    const fadeIn = Math.max(0.001, Math.max(0, opts.fadeInMs ?? 0) / 1000);
    env.gain.setValueAtTime(0, now);
    env.gain.linearRampToValueAtTime(target, now + fadeIn);

    // Optional ONE-TIME muffled→clear lowpass sweep, sitting after the source/mix.
    let filter: BiquadFilterNode | null = null;
    let head: AudioNode = env;
    if (opts.lowpassFromHz !== undefined && opts.lowpassToHz !== undefined) {
      filter = this.ctx.createBiquadFilter();
      filter.type = "lowpass";
      const sweep = Math.max(0.001, Math.max(0, opts.lowpassSweepMs ?? 0) / 1000);
      filter.frequency.setValueAtTime(opts.lowpassFromHz, now);
      filter.frequency.linearRampToValueAtTime(opts.lowpassToHz, now + sweep);
      filter.connect(env);
      head = filter;
    }
    env.connect(this.ambienceBus);

    if (opts.loop) {
      // Crossfading loop: instances ping-pong through `mix` so each seam is a
      // short blend (no gap, no click — and AAC's encoder padding is hidden).
      const mix = this.ctx.createGain();
      mix.connect(head);
      const crossfadeSec = Math.min(
        Math.max(0, (opts.crossfadeMs ?? AMBIENCE_XFADE_MS) / 1000),
        buffer.duration / 2,
      );
      this.ambienceCues.set(name, {
        env,
        filter,
        mix,
        instances: new Set(),
        loopTimer: null,
        buffer,
        durationSec: buffer.duration,
        crossfadeSec,
      });
      this.scheduleLoopInstance(name, now);
    } else {
      // One-shot: a single instance straight through the chain.
      const source = this.ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(head);
      const cue: AmbienceCue = {
        env,
        filter,
        mix: null,
        instances: new Set(),
        loopTimer: null,
        buffer,
        durationSec: buffer.duration,
        crossfadeSec: 0,
      };
      const inst = { source, gain: null };
      cue.instances.add(inst);
      this.ambienceCues.set(name, cue);
      source.onended = () => {
        cue.instances.delete(inst);
        try {
          source.disconnect();
        } catch {
          /* already disconnected */
        }
        if (this.ambienceCues.get(name) === cue && cue.instances.size === 0) {
          this.ambienceCues.delete(name);
          try {
            filter?.disconnect();
            env.disconnect();
          } catch {
            /* already disconnected */
          }
        }
      };
      source.start(now);
    }
  }

  /** Schedule one crossfade instance of a looping cue at audio time `when`, then
   *  chain the next ~(duration − crossfade) later so the loop blends seamlessly. */
  private scheduleLoopInstance(name: string, when: number): void {
    if (!this.ctx) return;
    const cue = this.ambienceCues.get(name);
    if (!cue || !cue.mix) return;
    const at = Math.max(when, this.ctx.currentTime + 0.02);
    const D = cue.durationSec;
    const X = cue.crossfadeSec;
    const period = Math.max(0.05, D - X);

    const source = this.ctx.createBufferSource();
    source.buffer = cue.buffer;
    const gain = this.ctx.createGain();
    source.connect(gain);
    gain.connect(cue.mix);
    // Crossfade envelope: in over X, hold at unity, out over X ending at at+D.
    gain.gain.setValueAtTime(0, at);
    if (X > 0) {
      gain.gain.linearRampToValueAtTime(1, at + X);
      gain.gain.setValueAtTime(1, at + period);
      gain.gain.linearRampToValueAtTime(0, at + D);
    } else {
      gain.gain.setValueAtTime(1, at);
    }
    const inst = { source, gain };
    cue.instances.add(inst);
    source.onended = () => {
      cue.instances.delete(inst);
      try {
        source.disconnect();
        gain.disconnect();
      } catch {
        /* already disconnected */
      }
    };
    source.start(at);
    source.stop(at + D + 0.05);

    // Chain the next instance a touch before this one's crossfade-out begins.
    cue.loopTimer = setTimeout(
      () => {
        cue.loopTimer = null;
        this.scheduleLoopInstance(name, at + period);
      },
      Math.max(0, period * 1000 - 120),
    );
  }

  /** (Re)start any desired ambience loops once the context is running. */
  private flushDesiredAmbience(): void {
    if (!this.ctx || this.ctx.state !== "running") return;
    for (const [name, opts] of this.desiredAmbience) {
      if (!this.ambienceCues.has(name)) {
        this.trace("ambience.flush", name);
        this.startAmbienceCue(name, opts);
      }
    }
  }

  /** Fade an ambience cue out over `fadeOutMs`, then stop + release every
   *  instance (and stop re-asserting it on resume). */
  stopAmbience(name: string, fadeOutMs = 400): void {
    this.desiredAmbience.delete(name);
    const cue = this.ambienceCues.get(name);
    if (!cue) return;
    this.ambienceCues.delete(name);
    if (cue.loopTimer) {
      clearTimeout(cue.loopTimer);
      cue.loopTimer = null;
    }
    const release = (): void => {
      for (const i of cue.instances) {
        try {
          i.source.stop();
        } catch {
          /* already stopped */
        }
        try {
          i.source.disconnect();
          i.gain?.disconnect();
        } catch {
          /* already disconnected */
        }
      }
      cue.instances.clear();
      try {
        cue.mix?.disconnect();
        cue.filter?.disconnect();
        cue.env.disconnect();
      } catch {
        /* already disconnected */
      }
    };
    if (!this.ctx) {
      release();
      return;
    }
    const now = this.ctx.currentTime;
    const secs = Math.max(0, fadeOutMs) / 1000;
    cue.env.gain.cancelScheduledValues(now);
    cue.env.gain.setValueAtTime(cue.env.gain.value, now);
    cue.env.gain.linearRampToValueAtTime(0, now + secs);
    const timer = setTimeout(() => {
      this.ambienceStopTimers.delete(timer);
      release();
    }, Math.max(0, fadeOutMs));
    this.ambienceStopTimers.add(timer);
  }

  /** Hard-stop every ambience cue + clear pending timers (teardown). */
  stopAllAmbience(): void {
    this.desiredAmbience.clear();
    for (const t of this.ambienceStopTimers) clearTimeout(t);
    this.ambienceStopTimers.clear();
    for (const cue of this.ambienceCues.values()) {
      if (cue.loopTimer) clearTimeout(cue.loopTimer);
      for (const i of cue.instances) {
        try {
          i.source.stop();
        } catch {
          /* already stopped */
        }
        try {
          i.source.disconnect();
          i.gain?.disconnect();
        } catch {
          /* already disconnected */
        }
      }
      try {
        cue.mix?.disconnect();
        cue.filter?.disconnect();
        cue.env.disconnect();
      } catch {
        /* already disconnected */
      }
    }
    this.ambienceCues.clear();
  }

  /** Loading grit bed — fade in at boot start (the POST/loading terminal). */
  playBootGrit(): void {
    this.playAmbience("boot_grit", {
      loop: true,
      fadeInMs: GRIT_FADE_IN_MS,
      gain: GRIT_GAIN,
    });
  }

  /** Loading grit bed — fade out at the boot→title hand-off (no title overlap). */
  stopBootGrit(): void {
    this.stopAmbience("boot_grit", GRIT_FADE_OUT_MS);
  }

  /** Alarm sting on the first red threat line — one-shot, runs to its end. */
  playThreatSting(): void {
    this.playAmbience("boot_threat", {
      loop: false,
      fadeInMs: STING_FADE_IN_MS,
      gain: STING_GAIN,
    });
  }

  destroy(): void {
    this.cancelFadeOut();
    this.clearThemeOutroTimer();
    this.stopMusic();
    // Task-001 (2026-ingame-music-rework, AC-6) — `stopMusic()` above already
    // halts the composer's scheduler, but `destroy()` (not `stop()`) is what
    // releases its graph nodes for good (the AA-19 lesson: a scheduler timer
    // must always have a `destroy()`-reachable clear path, not rely on the
    // caller having stopped it first).
    this.composer?.destroy();
    this.stopAllAmbience();
    this.stopHeartbeat();
    // Stop all active SFX voices
    for (const voices of this.activeVoices.values()) {
      for (const v of voices) {
        try {
          v.stop();
        } catch {
          /* already stopped */
        }
      }
    }
    this.activeVoices.clear();
    if (this.uiClickCleanup) {
      this.uiClickCleanup();
      this.uiClickCleanup = null;
    }
    if (this.uiHoverCleanup) {
      this.uiHoverCleanup();
      this.uiHoverCleanup = null;
    }
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers = [];
    if (this.disarmGestureResume) {
      this.disarmGestureResume();
      this.disarmGestureResume = null;
    }
    if (this.ctx) {
      void this.ctx.close();
      this.ctx = null;
    }
  }

  /**
   * S195/BL-283 — the near-loss heartbeat, band-driven (0 = off · 1..3 =
   * 800/650/520 ms cadence). Idempotent per band; the run-end lifecycle
   * handlers (game.over/won/restart/returnToMenu) remain the belt-and-braces
   * stop. Render-side audio only — no sim access.
   */
  setNearLossLevel(band: 0 | 1 | 2 | 3): void {
    if (band === this.nearLossBand_) return;
    this.nearLossBand_ = band;
    this.stopHeartbeat();
    if (band > 0) {
      this.heartbeatInterval = setInterval(
        () => this.playSfx("heartbeat"),
        NEAR_LOSS_HEARTBEAT_MS[band],
      );
    }
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /** Lifecycle stop (game.over/won/restart/menu): silence AND re-arm band 0. */
  private stopHeartbeatAndReset(): void {
    this.nearLossBand_ = 0;
    this.stopHeartbeat();
  }

  private subscribe(): void {
    this.on("tower.placed", () => this.playSfx("tower_place"));
    this.on("tower.sold", () => this.playSfx("tower_sell")); // CR-25
    this.on("tower.upgraded", () => this.playSfx("tower_upgrade")); // CR-25
    this.on("tower.fired", (d: unknown) => {
      const ev = d as
        | { tower?: { col?: number; element?: string } }
        | undefined;
      const element = ev?.tower?.element ?? "fire";
      // BL-120 fire-budget: throttle per element so a storm becomes a texture,
      // not a wall of identical pings. Keyed by element (each keeps its own
      // clock) so distinct elements still layer.
      const fireNow = Date.now();
      const last = this.lastFireSoundTime_.get(element) ?? 0;
      if (fireNow - last < FIRE_THROTTLE_MS) return;
      // S143 / BL-129: board-wide density throttle on top of the per-element
      // floor. Prune the rolling window, then drop a deterministic stride of
      // fires once the live rate crosses the threshold. Early boards never see
      // it (the threshold sits above their natural rate); W29+ storms enter the
      // bed gently. Per-element character is preserved because the drop is
      // counter-based (every 2nd / every 3rd), not random.
      const cutoff = fireNow - FIRE_DENSITY_WINDOW_MS;
      while (
        this.fireTimestamps_.length > 0 &&
        this.fireTimestamps_[0]! < cutoff
      ) {
        this.fireTimestamps_.shift();
      }
      const liveRate = this.fireTimestamps_.length;
      if (liveRate >= FIRE_DENSITY_HARD_THRESHOLD) {
        // 2-of-3 dropped → 1/3 of fires survive
        this.fireDensityCounter_ = (this.fireDensityCounter_ + 1) % 3;
        if (this.fireDensityCounter_ !== 0) return;
      } else if (liveRate >= FIRE_DENSITY_DROP_THRESHOLD) {
        // 1-of-2 dropped → 1/2 of fires survive
        this.fireDensityCounter_ = (this.fireDensityCounter_ + 1) % 2;
        if (this.fireDensityCounter_ !== 0) return;
      }
      this.fireTimestamps_.push(fireNow);
      this.lastFireSoundTime_.set(element, fireNow);
      const col = ev?.tower?.col ?? 10;
      const worldX = col * 48 + 24;
      const sfxKey = `tower_fire_${element}`;
      this.playSfx(this.buffers.has(sfxKey) ? sfxKey : "tower_shoot", worldX);
    });
    this.on("enemy.killed", (d: unknown) => {
      const now = Date.now();
      if (now - this.lastDeathSoundTime_ < 200) return;
      this.lastDeathSoundTime_ = now;
      const ev = d as { enemy?: { x?: number } } | undefined;
      this.playSfx("enemy_death", ev?.enemy?.x);
    });
    this.on("projectile.hit", (d: unknown) => {
      const now = Date.now();
      if (now - this.lastHitSoundTime_ < 100) return;
      this.lastHitSoundTime_ = now;
      const ev = d as
        | { projectile?: { x?: number; element?: string } }
        | undefined;
      const element = ev?.projectile?.element ?? "fire";
      const worldX = ev?.projectile?.x;
      const sfxKey = `hit_${element}`;
      this.playSfx(this.buffers.has(sfxKey) ? sfxKey : "enemy_death", worldX);
    });
    this.on("enemy.spawned", (d: unknown) => {
      // Task-011 (AC-14/O-6, D-325) — staged A→C mass-spawn fix.
      const now = Date.now();
      // Stage C first: prune + extend the rolling spawn-rate window on EVERY
      // spawn (not just ones that pass the cooldown) so density is measured
      // against the real event rate, matching the fire-budget's rationale.
      const cutoff = now - SPAWN_DENSITY_WINDOW_MS;
      while (
        this.spawnTimestamps_.length > 0 &&
        this.spawnTimestamps_[0]! < cutoff
      ) {
        this.spawnTimestamps_.shift();
      }
      this.spawnTimestamps_.push(now);
      const liveRate = this.spawnTimestamps_.length;

      if (liveRate > SPAWN_DENSITY_THRESHOLD) {
        // Above the switchover: suppress the one-shot entirely — the burst
        // reads as ONE wave-surge (music-energy pressure), not N blips.
        this.triggerSpawnSurge(liveRate);
        return;
      }

      // Stage A (below the switchover): parity cooldown + pitch variation,
      // the same shape as `lastDeathSoundTime_`/`lastHitSoundTime_` above.
      if (now - this.lastSpawnSoundTime_ < SPAWN_THROTTLE_MS) return;
      this.lastSpawnSoundTime_ = now;
      const ev = d as { enemy?: { x?: number } } | undefined;
      // Small render-local playbackRate jitter (never the sim RNG) — the
      // `detuneRange` on the profile ALSO applies per-play (playSfx, above),
      // so a spawn burst layers two independent variation axes.
      const rate = 1 + (Math.random() * 2 - 1) * SPAWN_RATE_JITTER;
      this.playSfx("enemy_spawn", ev?.enemy?.x, { rate });
    });
    this.on("enemy.reachedEnd", () => this.playSfx("enemy_leak"));
    // BL-152 (S154): the "NEW THREAT DETECTED" telegraph stinger — a per-act new
    // archetype (Jammer / Overseer) debuts. Reuse the dramatic boss stinger so no
    // new audio asset ships (size-tight). Render/audio only — never in the sim.
    this.on("wave.newthreat.incoming", () => this.playSfx("boss_stinger"));
    this.on("boss.spawned", (d: unknown) => {
      // S143 / BL-136 (PT-40 #1): the loud boss_spawn chord must play only for
      // the *real* bosses (finalBoss + the spawnAdds boss path), not the HP≥500
      // elites the owner saw as the CORE BREAKER over-firing. The non-final
      // heavies still get the regular enemy_spawn (low gain, REPEAT_TIER), which
      // is plenty of indication on its own; the calmed banner (combatHandlers)
      // carries the visual notice. BL-163 (S163) retired the `maxHp >= 500`
      // boss heuristic at the source, so `boss.spawned` now fires only for real
      // bosses anyway — this guard is now belt-and-suspenders, kept on purpose.
      const ev = d as
        | { boss?: { x?: number; special?: { type?: string } } }
        | undefined;
      const t = ev?.boss?.special?.type;
      const isRealBoss = t === "finalBoss" || t === "spawnAdds";
      if (!isRealBoss) return;
      this.playSfx("boss_spawn", ev?.boss?.x);
    });
    this.on("boss.phaseChange", () => this.playSfx("boss_phase"));
    this.on("boss.defeated", (d: unknown) => {
      // BL-160 (S160 audit): mirror the boss.spawned guard above — restrict the
      // dramatic boss_defeat fanfare to the *real* bosses (the asymmetric twin of
      // the PT-40 #1 boss.spawned fix). Pre-BL-163 `isBoss()` keyed off (scaled)
      // maxHp ≥ 500 — which most enemies crossed by mid-game — so without this
      // guard the fanfare fired for every heavy/scaled death (even the W35 warm-up
      // scouts). BL-163 (S163) retired that heuristic at the source, so this guard
      // is now belt-and-suspenders. Audio-only.
      const ev = d as
        | { boss?: { x?: number; special?: { type?: string } } }
        | undefined;
      const t = ev?.boss?.special?.type;
      const isRealBoss = t === "finalBoss" || t === "spawnAdds";
      if (!isRealBoss) return;
      this.playSfx("boss_defeat", ev?.boss?.x);
    });
    this.on("game.won", () => {
      // Stop the low-HP heartbeat loop — winning the final wave at <=3 HP would
      // otherwise leave it ticking forever on the victory screen (no further
      // mainframe.hit to clear it).
      this.stopHeartbeat();
      this.playSfx("victory_fanfare");
    });
    // RESTART (HUD/key, reachable mid-run) must also clear a low-HP heartbeat so
    // it doesn't carry into the fresh full-HP run until the next hit.
    this.on("game.restart", () => this.stopHeartbeatAndReset());
    this.on("mainframe.hit", (d: unknown) => {
      const ev = d as { damage?: number; livesLeft?: number } | undefined;
      const dmg = ev?.damage ?? 1;
      if (dmg >= 5) this.playSfx("mainframe_hit_critical");
      else if (dmg >= 3) this.playSfx("mainframe_hit_heavy");
      else if (dmg >= 2) this.playSfx("mainframe_hit_medium");
      else this.playSfx("mainframe_hit_small");
      // CR-39 heartbeat ownership moved to setNearLossLevel (S195/BL-283):
      // the NearLossDirector polls lives every frame, so the beat also STOPS
      // on an addLives rescue (it used to keep beating until the NEXT hit)
      // and its cadence escalates 800→650→520 ms as the band deepens.
    });
    this.on("game.over", () => {
      this.stopHeartbeatAndReset();
      this.playSfx("game_over");
    });
    this.on("game.returnToMenu", () => this.stopHeartbeatAndReset());
    this.on("wave.started", () => this.playSfx("wave_start"));
    // S195/BL-281 (S195-F06): the combo-milestone sting — BL-241 shipped the
    // banner + flash and a comment claimed the banner "funnels the audio
    // sting", but no cue existed. A short rising chord, repeat-tier routed so
    // any boss/critical cue in the same instant ducks it (anti-fight law #8).
    this.on("combo.milestone", (d: unknown) => {
      const mult = (d as { mult?: number })?.mult ?? 5;
      // ×5 → base, ×7 → +2 semitones, ×10 → +4 — the ladder audibly climbs.
      const semis = mult >= 10 ? 4 : mult >= 7 ? 2 : 0;
      this.playSfx("combo_milestone", undefined, {
        rate: Math.pow(2, semis / 12),
      });
    });
    this.on("wave.completed", () => this.playSfx("wave_complete"));
    this.on("boss.wave.incoming", () => this.playSfx("boss_stinger")); // CR-27
    // CR-H3 (LD-22.1-2): stealth-wave warning audio. Re-uses boss_stinger
    // SFX (no new asset added per LD-22-4 / out-of-scope). If a dedicated
    // signal-pulse SFX is added later, swap the playSfx key.
    this.on("wave.stealth.incoming", () => this.playSfx("boss_stinger"));
    // S141 / D-236: ECHELON mutation telegraph re-uses the stinger (no new asset).
    this.on("wave.mutation.incoming", () => this.playSfx("boss_stinger"));
    this.on("threat.pulse", () => this.playSfx("boss_stinger"));
    this.on("draft.cardSelected", (d: unknown) => {
      const ev = d as { card?: { rarity?: string } } | undefined;
      const rarity = ev?.card?.rarity ?? "common";
      const sfxKey = `card_equip_${rarity}`;
      // Fall back to generic if rarity-specific not in manifest
      if (this.buffers.has(sfxKey)) {
        this.playSfx(sfxKey);
      } else {
        this.playSfx("card_equip");
      }
    });
    this.on("countdown.tick", () => this.playSfx("countdown_beep"));
    this.on("wave.manualStart", () => this.playSfx("wave_boot"));
    this.on("breach.towerDestroyed", () => {
      this.playSfx("tower_destroyed");
      this.playSfx("tower_destroyed_crash");
      this.playSfx("path_distortion");
    });
    this.on("signal.overload", () => {
      this.playSfx("signal_overload");
      this.playSfx("signal_overload_hum");
    });
    this.on("path.revealStarted", () => this.playSfx("path_stream"));
    // BL-68 P4: close the silent gaps
    this.on("placement.rejected", () => this.playSfx("ui_error"));
    this.on("skill.activated", () => this.playSfx("skill_activate"));
    this.on("firewall.placed", () => this.playSfx("firewall_place"));
    this.on("firewall.sold", () => this.playSfx("firewall_sell"));
    this.on("firewall.destroyed", () => this.playSfx("firewall_destroy"));
    this.on("payload.armed", () => this.playSfx("payload_arm"));
    this.on("payload.fired", () => this.playSfx("payload_fire"));
    this.on("payload.detonated", (d: unknown) => {
      const ev = d as { x?: number } | undefined;
      this.playSfx("payload_detonate", ev?.x);
    });
    this.on("achievement.unlocked", () => this.playSfx("achievement_unlock"));
    this.on("synergy.triggered", () => this.playSfx("synergy_trigger"));
    this.on("economy.earned", () => this.playSfx("economy_tick"));
    this.on("economy.spent", () => this.playSfx("economy_tick"));
    this.on("game.paused", () => this.playSfx("pause_in"));
    this.on("game.resumed", () => this.playSfx("pause_out"));
    this.on("game.activateTimeWarp", () => this.playSfx("time_warp"));
    this.on("sfx:ui_click", () => this.playSfx("ui_click"));
    this.on("sfx:ui_open", () => this.playSfx("ui_open"));
    this.on("sfx:ui_close", () => this.playSfx("ui_close"));
    this.on("sfx:achievement_unlock", () => this.playSfx("achievement_unlock"));
    this.on("sfx:skill_activate", () => this.playSfx("skill_activate"));
    // Global UI feedback: every button (incl. `data-sfx-btn` opt-ins, e.g. the
    // pregame cards) speaks a soft glass tick on hover + click.
    this.setupUIClickSound();
    this.setupUIHoverSound();
    this.on("audio.toggleMute", () => this.toggleMute());
    this.on("audio.toggleMusic", () => this.toggleMusic());
    this.on("audio.toggleMaster", () => this.toggleMaster());
    this.on("audio.setSfxVolume", (d: unknown) =>
      this.setSfxVolume(d as number),
    );
    this.on("audio.setMusicVolume", (d: unknown) =>
      this.setMusicVolume(d as number),
    );
  }

  /** Attach a global click listener so every button (incl. `data-sfx-btn`
   *  opt-ins) speaks a soft glass click. */
  private setupUIClickSound(): void {
    if (typeof document === "undefined") return;
    const handler = (e: Event): void => {
      const el = (e.target as HTMLElement)?.closest?.(UI_BUTTON_SELECTOR);
      if (el) this.playSfx("ui_click");
    };
    document.addEventListener("click", handler, { capture: true });
    this.uiClickCleanup = () =>
      document.removeEventListener("click", handler, { capture: true });
  }

  /** Attach a global pointer listener so a button speaks a soft glass tick when
   *  the pointer FIRST enters it — deduped per element, lightly throttled. */
  private setupUIHoverSound(): void {
    if (typeof document === "undefined") return;
    const handler = (e: Event): void => {
      const el =
        (e.target as HTMLElement)?.closest?.(UI_BUTTON_SELECTOR) ?? null;
      if (!el) {
        this.lastHoverEl_ = null; // left the button → re-entering replays
        return;
      }
      if (el === this.lastHoverEl_) return; // still on the same button
      const now = Date.now();
      if (now - this.lastHoverTime_ < UI_HOVER_THROTTLE_MS) return;
      this.lastHoverEl_ = el;
      this.lastHoverTime_ = now;
      this.playSfx("ui_hover");
    };
    document.addEventListener("pointerover", handler, { capture: true });
    this.uiHoverCleanup = () =>
      document.removeEventListener("pointerover", handler, { capture: true });
  }

  private on(event: keyof AppEventMap, handler: (d?: unknown) => void): void {
    this.unsubscribers.push(
      this.bus.on(event, handler as (d: unknown) => void),
    );
  }
}
